/**
 * simulator/mockData.js
 * Genera datos mock sector-aware para el simulador.
 * Basado en demoMode.js de appointments pero adaptado para múltiples sectores.
 */

import dayjs from "dayjs";
import { getVendorDemoPhone, DAVID_ROMERO_PHONE } from "./utils/vendorPhoneMap";
import { getSectorBookingInfo, MOCK_BOOKING_CODE } from "./components/personal/whatsappPosterCopy";
import { buildFoodChatList, buildFoodChatMessages } from "./pages/foodorders/foodChatsMock";
import { alumnosSemanaLlena, practicasSemanaLlena } from "./pages/autoescuela/semanaLlenaDemo";
import { buildPeluqueriaAgenda } from "./utils/peluqueriaAgendaDemo";

// CAP de demostración: caducidad a `dias` desde hoy (obtención 5 años antes).
// Caducidad de documentos del alumno (8/10/2026): fecha relativa a hoy, como el CAP (la demo nunca caduca).
const fechaDemo = (dias) => dayjs().add(dias, "day").format("YYYY-MM-DD");
const capDemo = (dias) => ({
  caducidad: dayjs().add(dias, "day").format("YYYY-MM-DD"),
  obtencion: dayjs().add(dias, "day").subtract(5, "year").format("YYYY-MM-DD"),
});
const SIM_COMPANY_ID = "simulator-demo";

/* ─── Datos por sector ─────────────────────────────────────── */

const SECTOR_DATA = {
  peluquerias: {
    label: "Peluquería",
    services: [
      { id: "s1", service_id: "sim-pel-s1", name: "Corte Mujer", duration: 45, total_duration: 45, price: 25, color: "#F59E0B", gender: "mujer" },
      { id: "s2", service_id: "sim-pel-s2", name: "Corte Caballero", duration: 30, total_duration: 30, price: 18, color: "#3B82F6", gender: "hombre" },
      { id: "s3", service_id: "sim-pel-s3", name: "Corte Niñ@", duration: 30, total_duration: 30, price: 15, color: "#10B981", gender: "unisex" },
      { id: "s4", service_id: "sim-pel-s4", name: "Corte y Lavado", duration: 45, total_duration: 45, price: 28, color: "#EF4444", gender: "mujer" },
      // Tinte y mechas intercalan: durante la exposición la profesional atiende a otra clienta (agenda de demo).
      { id: "s5", service_id: "sim-pel-s5", name: "Tinte", duration: 90, total_duration: 90, work_duration: 30, processing_duration: 35, allow_intercalation: true, price: 45, color: "#8B5CF6", gender: "mujer" },
      { id: "s6", service_id: "sim-pel-s6", name: "Mechas", duration: 120, total_duration: 120, work_duration: 45, processing_duration: 40, allow_intercalation: true, price: 65, color: "#EC4899", gender: "mujer" },
      { id: "s7", service_id: "sim-pel-s7", name: "Peinado", duration: 45, total_duration: 45, price: 25, color: "#14B8A6", gender: "mujer" },
      { id: "s8", service_id: "sim-pel-s8", name: "Tratamiento Hidratación", duration: 40, total_duration: 40, price: 30, color: "#06B6D4", gender: "unisex" },
      { id: "s9", service_id: "sim-pel-s9", name: "Lavado", duration: 15, total_duration: 15, price: 8, color: "#F97316", gender: "unisex" },
      { id: "s10", service_id: "sim-pel-s10", name: "Manicura", duration: 45, total_duration: 45, price: 20, color: "#F472B6", gender: "mujer" },
      { id: "s11", service_id: "sim-pel-s11", name: "Pedicura", duration: 60, total_duration: 60, price: 25, color: "#C084FC", gender: "mujer" },
    ],
    clientNames: ["Carmen García", "María López", "Laura Martínez", "Isabel Rodríguez", "Ana Sánchez",
      "Elena González", "Sofía Hernández", "Paula Torres", "Lucía Díaz", "Marta Moreno",
      "Javier Jiménez", "Carlos Ruiz", "David Romero", "Pedro Alonso", "Miguel Navarro",
      // Más clientela para que la agenda de demo (peluqueriaAgendaDemo) no repita nombre en el mismo día.
      "Rocío Vidal", "Nuria Castillo", "Pilar Ortega", "Silvia Romero", "Beatriz Gil",
      "Noelia Serrano", "Raquel Molina", "Inés Delgado", "Cristina Vega", "Alba Herrero",
      "Sergio Prieto", "Hugo Martín", "Álvaro Cano"],
    workers: [
      { id: "w1", worker_id: "sim-pel-w1", user_id: "sim-pel-w1", name: "Laura Sánchez", email: "laura@peluqueria.com", phone: "+34612000001", active: true },
      { id: "w2", worker_id: "sim-pel-w2", user_id: "sim-pel-w2", name: "Carlos Ruiz", email: "carlos@verticetrade.es", phone: "+34612000002", active: true },
      { id: "w3", worker_id: "sim-pel-w3", user_id: "sim-pel-w3", name: "Marta Jiménez", email: "marta@peluqueria.com", phone: "+34612000003", active: true },
    ],
  },
  barberia: {
    label: "Barbería",
    services: [
      { id: "s1", service_id: "sim-barb-s1", name: "Corte Normal", duration: 30, total_duration: 30, price: 18, color: "#3B82F6", gender: "hombre" },
      { id: "s2", service_id: "sim-barb-s2", name: "Corte Básico", duration: 20, total_duration: 20, price: 15, color: "#60A5FA", gender: "hombre" },
      { id: "s3", service_id: "sim-barb-s3", name: "Corte Premium (+ Lavado)", duration: 45, total_duration: 45, price: 25, color: "#10B981", gender: "hombre" },
      { id: "s4", service_id: "sim-barb-s4", name: "Barba - Perfilado", duration: 15, total_duration: 15, price: 10, color: "#EC4899", gender: "hombre" },
      { id: "s5", service_id: "sim-barb-s5", name: "Barba - Completa", duration: 30, total_duration: 30, price: 18, color: "#D946EF", gender: "hombre" },
      { id: "s6", service_id: "sim-barb-s6", name: "Barba - Tratamiento Spa", duration: 45, total_duration: 45, price: 25, color: "#8B5CF6", gender: "hombre" },
      { id: "s7", service_id: "sim-barb-s7", name: "Tinte - Camuflaje Canas", duration: 30, total_duration: 30, price: 20, color: "#F97316", gender: "hombre" },
      { id: "s8", service_id: "sim-barb-s8", name: "Tinte - Color Fantasía", duration: 60, total_duration: 60, price: 35, color: "#F59E0B", gender: "hombre" },
      { id: "s9", service_id: "sim-barb-s9", name: "Tinte - Barba", duration: 20, total_duration: 20, price: 15, color: "#06B6D4", gender: "hombre" },
      { id: "s10", service_id: "sim-barb-s10", name: "Rapado - Solo máquina", duration: 15, total_duration: 15, price: 10, color: "#94A3B8", gender: "hombre" },
      { id: "s11", service_id: "sim-barb-s11", name: "Rapado - con Barba", duration: 40, total_duration: 40, price: 22, color: "#64748B", gender: "hombre" },
      { id: "s12", service_id: "sim-barb-s12", name: "Cejas - Limpieza", duration: 15, total_duration: 15, price: 8, color: "#6B7280", gender: "hombre" },
      { id: "s13", service_id: "sim-barb-s13", name: "Cejas - Diseño", duration: 20, total_duration: 20, price: 12, color: "#4B5563", gender: "hombre" },
      { id: "s14", service_id: "sim-barb-s14", name: "Facial - Exfoliación", duration: 20, total_duration: 20, price: 15, color: "#14B8A6", gender: "hombre" },
      { id: "s15", service_id: "sim-barb-s15", name: "Facial - Mascarilla Negra", duration: 30, total_duration: 30, price: 18, color: "#0D9488", gender: "hombre" },
    ],
    // En barbería sólo nombres masculinos para que el simulador sea coherente con el sector
    clientNames: [
      "Javier Jiménez",
      "Carlos Ruiz",
      "David Romero",
      "Pedro Alonso",
      "Miguel Navarro",
      "Antonio Pérez",
      "José Luis García",
      "Francisco Ruiz",
      "Manuel López",
      "Pedro Sánchez",
      "Javier Hernández",
      "Carlos Moreno",
      "Diego Romero",
      "Alejandro Castro",
      "Rafael Sánchez",
    ],
    workers: [
      { id: "w1", worker_id: "sim-pel-w1", user_id: "sim-pel-w1", name: "Laura Sánchez", email: "laura@peluqueria.com", phone: "+34612000001", active: true },
      { id: "w2", worker_id: "sim-pel-w2", user_id: "sim-pel-w2", name: "Carlos Ruiz", email: "carlos@verticetrade.es", phone: "+34612000002", active: true },
      { id: "w3", worker_id: "sim-pel-w3", user_id: "sim-pel-w3", name: "Sergio Molina", email: "sergio@barberia.com", phone: "+34612000003", active: true },
    ],
  },
  estetica: {
    label: "Estética",
    services: [
      // Faciales
      { id: "s1",  service_id: "sim-est-s1",  name: "Limpieza facial profunda",   duration: 60,  total_duration: 60,  price: 55,  color: "#EC4899", group: "Facial" },
      { id: "s2",  service_id: "sim-est-s2",  name: "Hidratación facial",          duration: 45,  total_duration: 45,  price: 40,  color: "#F472B6", group: "Facial" },
      { id: "s3",  service_id: "sim-est-s3",  name: "Tratamiento antiedad",        duration: 75,  total_duration: 75,  price: 80,  color: "#DB2777", group: "Facial" },
      { id: "s4",  service_id: "sim-est-s4",  name: "Peeling facial",              duration: 45,  total_duration: 45,  price: 50,  color: "#BE185D", group: "Facial" },
      { id: "s5",  service_id: "sim-est-s5",  name: "Microdermoabrasión",          duration: 50,  total_duration: 50,  price: 60,  color: "#9D174D", group: "Facial" },
      { id: "s6",  service_id: "sim-est-s6",  name: "Tratamiento acné",            duration: 60,  total_duration: 60,  price: 55,  color: "#831843", group: "Facial" },
      { id: "s7",  service_id: "sim-est-s7",  name: "Radiofrecuencia facial",      duration: 60,  total_duration: 60,  price: 90,  color: "#E879F9", group: "Facial" },
      { id: "s8",  service_id: "sim-est-s8",  name: "Mesoterapia facial",          duration: 45,  total_duration: 45,  price: 75,  color: "#C026D3", group: "Facial" },
      // Corporales
      { id: "s9",  service_id: "sim-est-s9",  name: "Masaje relajante",            duration: 60,  total_duration: 60,  price: 50,  color: "#3B82F6", group: "Corporal" },
      { id: "s10", service_id: "sim-est-s10", name: "Masaje reafirmante",          duration: 60,  total_duration: 60,  price: 55,  color: "#2563EB", group: "Corporal" },
      { id: "s11", service_id: "sim-est-s11", name: "Drenaje linfático manual",    duration: 60,  total_duration: 60,  price: 60,  color: "#1D4ED8", group: "Corporal" },
      { id: "s12", service_id: "sim-est-s12", name: "Envoltura corporal",          duration: 90,  total_duration: 90,  price: 70,  color: "#06B6D4", group: "Corporal" },
      { id: "s13", service_id: "sim-est-s13", name: "Tratamiento reductor",        duration: 60,  total_duration: 60,  price: 65,  color: "#0891B2", group: "Corporal" },
      { id: "s14", service_id: "sim-est-s14", name: "Presoterapia",                duration: 45,  total_duration: 45,  price: 45,  color: "#0E7490", group: "Corporal" },
      // Depilación
      { id: "s15", service_id: "sim-est-s15", name: "Depilación cera piernas completas", duration: 50, total_duration: 50, price: 30, color: "#10B981", group: "Depilación" },
      { id: "s16", service_id: "sim-est-s16", name: "Depilación cera medias piernas",    duration: 30, total_duration: 30, price: 18, color: "#059669", group: "Depilación" },
      { id: "s17", service_id: "sim-est-s17", name: "Depilación cera axilas",             duration: 15, total_duration: 15, price: 10, color: "#047857", group: "Depilación" },
      { id: "s18", service_id: "sim-est-s18", name: "Depilación cera ingles",             duration: 20, total_duration: 20, price: 12, color: "#065F46", group: "Depilación" },
      { id: "s19", service_id: "sim-est-s19", name: "Depilación cera labio superior",     duration: 10, total_duration: 10, price: 8,  color: "#34D399", group: "Depilación" },
      { id: "s20", service_id: "sim-est-s20", name: "Depilación cera brazos",             duration: 35, total_duration: 35, price: 20, color: "#6EE7B7", group: "Depilación" },
      { id: "s21", service_id: "sim-est-s21", name: "Depilación cera cuerpo completo",    duration: 90, total_duration: 90, price: 65, color: "#A7F3D0", group: "Depilación" },
      // Manicura y Pedicura
      { id: "s22", service_id: "sim-est-s22", name: "Manicura clásica",            duration: 40,  total_duration: 40,  price: 18,  color: "#F59E0B", group: "Manos y Pies" },
      { id: "s23", service_id: "sim-est-s23", name: "Manicura semipermanente",     duration: 60,  total_duration: 60,  price: 28,  color: "#D97706", group: "Manos y Pies" },
      { id: "s24", service_id: "sim-est-s24", name: "Manicura con gel",            duration: 70,  total_duration: 70,  price: 35,  color: "#B45309", group: "Manos y Pies" },
      { id: "s25", service_id: "sim-est-s25", name: "Pedicura clásica",            duration: 55,  total_duration: 55,  price: 25,  color: "#FBBF24", group: "Manos y Pies" },
      { id: "s26", service_id: "sim-est-s26", name: "Pedicura spa",                duration: 75,  total_duration: 75,  price: 38,  color: "#FCD34D", group: "Manos y Pies" },
      { id: "s27", service_id: "sim-est-s27", name: "Pedicura semipermanente",     duration: 70,  total_duration: 70,  price: 35,  color: "#FDE68A", group: "Manos y Pies" },
      // Uñas
      { id: "s28", service_id: "sim-est-s28", name: "Uñas acrílicas",             duration: 90,  total_duration: 90,  price: 55,  color: "#F43F5E", group: "Uñas" },
      { id: "s29", service_id: "sim-est-s29", name: "Uñas de gel",                duration: 80,  total_duration: 80,  price: 50,  color: "#E11D48", group: "Uñas" },
      { id: "s30", service_id: "sim-est-s30", name: "Uñas de porcelana",          duration: 90,  total_duration: 90,  price: 60,  color: "#BE123C", group: "Uñas" },
      { id: "s31", service_id: "sim-est-s31", name: "Relleno uñas acrílicas",     duration: 60,  total_duration: 60,  price: 35,  color: "#9F1239", group: "Uñas" },
      { id: "s32", service_id: "sim-est-s32", name: "Retirada uñas acrílicas/gel",duration: 30,  total_duration: 30,  price: 15,  color: "#FB7185", group: "Uñas" },
      { id: "s33", service_id: "sim-est-s33", name: "French manicura",            duration: 50,  total_duration: 50,  price: 25,  color: "#FDA4AF", group: "Uñas" },
      { id: "s34", service_id: "sim-est-s34", name: "Nail art",                   duration: 30,  total_duration: 30,  price: 15,  color: "#FECDD3", group: "Uñas" },
      // Pestañas y Cejas
      { id: "s35", service_id: "sim-est-s35", name: "Diseño de cejas",            duration: 25,  total_duration: 25,  price: 15,  color: "#8B5CF6", group: "Pestañas y Cejas" },
      { id: "s36", service_id: "sim-est-s36", name: "Depilación de cejas",        duration: 15,  total_duration: 15,  price: 10,  color: "#7C3AED", group: "Pestañas y Cejas" },
      { id: "s37", service_id: "sim-est-s37", name: "Tinte de cejas",             duration: 20,  total_duration: 20,  price: 12,  color: "#6D28D9", group: "Pestañas y Cejas" },
      { id: "s38", service_id: "sim-est-s38", name: "Tinte de pestañas",          duration: 20,  total_duration: 20,  price: 12,  color: "#5B21B6", group: "Pestañas y Cejas" },
      { id: "s39", service_id: "sim-est-s39", name: "Extensión de pestañas",      duration: 120, total_duration: 120, price: 80,  color: "#A78BFA", group: "Pestañas y Cejas" },
      { id: "s40", service_id: "sim-est-s40", name: "Lifting de pestañas",        duration: 60,  total_duration: 60,  price: 55,  color: "#C4B5FD", group: "Pestañas y Cejas" },
      { id: "s41", service_id: "sim-est-s41", name: "Laminado de cejas",          duration: 45,  total_duration: 45,  price: 40,  color: "#DDD6FE", group: "Pestañas y Cejas" },
    ],
    clientNames: ["Sofía Romero", "David Romero", "Carmen García", "Valentina García", "Daniela Martínez",
      "Camila Rodríguez", "Isabella Sánchez", "Gabriela González", "Claudia Hernández", "Natalia Torres",
      "Patricia Díaz", "Cristina Moreno", "Beatriz Jiménez", "Alicia Ruiz", "Eva Romero"],
    workers: [
      { id: "w1", worker_id: "sim-est-w1", user_id: "sim-est-w1", name: "Sofía Romero", email: "sofia@estetica.com", phone: "+34613000001", active: true },
      { id: "w2", worker_id: "sim-est-w2", user_id: "sim-est-w2", name: "Andrea López", email: "andrea@estetica.com", phone: "+34613000002", active: true },
      { id: "w3", worker_id: "sim-est-w3", user_id: "sim-est-w3", name: "Nuria Vega", email: "nuria@estetica.com", phone: "+34613000003", active: true },
    ],
  },
  canina: {
    label: "Peluquería canina",
    services: [
      // Baño e higiene
      { id: "s1",  service_id: "sim-can-s1",  name: "Baño perro pequeño",          duration: 45,  total_duration: 45,  price: 22, color: "#38BDF8", group: "Baño" },
      { id: "s2",  service_id: "sim-can-s2",  name: "Baño perro mediano",          duration: 60,  total_duration: 60,  price: 28, color: "#0EA5E9", group: "Baño" },
      { id: "s3",  service_id: "sim-can-s3",  name: "Baño perro grande",           duration: 90,  total_duration: 90,  price: 38, color: "#0284C7", group: "Baño" },
      // Sin "Baño con champú especial": el champú es un EXTRA que combina con
      // cualquier talla, no una cuarta talla (feedback de Trini, 3/9/2026). Si un
      // cliente real lo pide, se modela como suplemento (is_supplement).
      // Corte y peluquería
      { id: "s5",  service_id: "sim-can-s5",  name: "Baño y corte pequeño",        duration: 75,  total_duration: 75,  price: 32, color: "#F59E0B", group: "Corte" },
      { id: "s6",  service_id: "sim-can-s6",  name: "Baño y corte mediano",        duration: 90,  total_duration: 90,  price: 40, color: "#D97706", group: "Corte" },
      { id: "s7",  service_id: "sim-can-s7",  name: "Baño y corte grande",         duration: 120, total_duration: 120, price: 52, color: "#B45309", group: "Corte" },
      { id: "s8",  service_id: "sim-can-s8",  name: "Corte de raza (estándar)",    duration: 90,  total_duration: 90,  price: 45, color: "#EA580C", group: "Corte" },
      { id: "s9",  service_id: "sim-can-s9",  name: "Corte a tijera",              duration: 90,  total_duration: 90,  price: 48, color: "#F97316", group: "Corte" },
      { id: "s10", service_id: "sim-can-s10", name: "Stripping (arrancado a mano)",duration: 120, total_duration: 120, price: 60, color: "#C2410C", group: "Corte" },
      { id: "s11", service_id: "sim-can-s11", name: "Deslanado",                   duration: 90,  total_duration: 90,  price: 42, color: "#FB923C", group: "Corte" },
      // Cuidados
      { id: "s12", service_id: "sim-can-s12", name: "Corte de uñas",               duration: 15,  total_duration: 15,  price: 8,  color: "#10B981", group: "Cuidados" },
      { id: "s13", service_id: "sim-can-s13", name: "Limpieza de oídos",           duration: 15,  total_duration: 15,  price: 8,  color: "#059669", group: "Cuidados" },
      { id: "s14", service_id: "sim-can-s14", name: "Limpieza dental",             duration: 30,  total_duration: 30,  price: 18, color: "#047857", group: "Cuidados" },
      { id: "s15", service_id: "sim-can-s15", name: "Tratamiento antiparasitario", duration: 30,  total_duration: 30,  price: 20, color: "#8B5CF6", group: "Cuidados" },
      { id: "s16", service_id: "sim-can-s16", name: "Tratamiento dermatológico",   duration: 45,  total_duration: 45,  price: 35, color: "#7C3AED", group: "Cuidados" },
      { id: "s17", service_id: "sim-can-s17", name: "Baño de gato",                duration: 60,  total_duration: 60,  price: 35, color: "#EC4899", group: "Cuidados" },
    ],
    clientNames: [
      "Javier Jiménez", "Carmen García", "David Romero", "Laura Sánchez", "Miguel Torres",
      "Elena Ruiz", "Antonio López", "Sara Moreno", "Pedro González", "Isabel Fernández",
      "Carlos Martínez", "Lucía Navarro", "Francisco Ruiz", "Ana Martínez", "Diego Romero",
    ],
    workers: [
      { id: "w1", worker_id: "sim-can-w1", user_id: "sim-can-w1", name: "Marta Peris", email: "marta@peluqueriacanina.com", phone: "+34617000001", active: true },
      { id: "w2", worker_id: "sim-can-w2", user_id: "sim-can-w2", name: "Raquel Soler", email: "raquel@peluqueriacanina.com", phone: "+34617000002", active: true },
      { id: "w3", worker_id: "sim-can-w3", user_id: "sim-can-w3", name: "Iván Beltrán", email: "ivan@peluqueriacanina.com", phone: "+34617000003", active: true },
    ],
  },
  tatuajes: {
    label: "Tatuajes",
    services: [
      // Espejo del catálogo de fábrica del sector en c_sector_config (grupos
      // Tatuaje / Piercing / Cuidados), con precios de referencia.
      { id: "s1",  service_id: "sim-tat-s1",  name: "Consulta y diseño",           duration: 30,  total_duration: 30,  price: 0,   color: "#64748B", group: "Tatuaje" },
      { id: "s2",  service_id: "sim-tat-s2",  name: "Sesión pequeña (hasta 2h)",   duration: 120, total_duration: 120, price: 150, color: "#334155", group: "Tatuaje" },
      { id: "s3",  service_id: "sim-tat-s3",  name: "Sesión mediana (2-4h)",       duration: 240, total_duration: 240, price: 280, color: "#1E293B", group: "Tatuaje" },
      { id: "s4",  service_id: "sim-tat-s4",  name: "Sesión completa (día)",       duration: 420, total_duration: 420, price: 480, color: "#0F172A", group: "Tatuaje" },
      { id: "s5",  service_id: "sim-tat-s5",  name: "Retoque",                     duration: 60,  total_duration: 60,  price: 0,   color: "#7C3AED", group: "Tatuaje" },
      { id: "s6",  service_id: "sim-tat-s6",  name: "Cover-up (consulta)",         duration: 45,  total_duration: 45,  price: 0,   color: "#8B5CF6", group: "Tatuaje" },
      { id: "s7",  service_id: "sim-tat-s7",  name: "Piercing sencillo",           duration: 30,  total_duration: 30,  price: 35,  color: "#EC4899", group: "Piercing" },
      { id: "s8",  service_id: "sim-tat-s8",  name: "Piercing complejo",           duration: 45,  total_duration: 45,  price: 55,  color: "#DB2777", group: "Piercing" },
      { id: "s9",  service_id: "sim-tat-s9",  name: "Revisión de cicatrización",   duration: 15,  total_duration: 15,  price: 0,   color: "#10B981", group: "Cuidados" },
    ],
    clientNames: [
      "Javier Jiménez", "Carmen García", "David Romero", "Laura Sánchez", "Miguel Torres",
      "Elena Ruiz", "Antonio López", "Sara Moreno", "Pedro González", "Isabel Fernández",
      "Carlos Martínez", "Lucía Navarro", "Francisco Ruiz", "Ana Martínez", "Diego Romero",
    ],
    workers: [
      { id: "w1", worker_id: "sim-tat-w1", user_id: "sim-tat-w1", name: "Iván Cortés", email: "ivan@estudiotatuajes.com", phone: "+34620000001", active: true },
      { id: "w2", worker_id: "sim-tat-w2", user_id: "sim-tat-w2", name: "Alba Ferrer", email: "alba@estudiotatuajes.com", phone: "+34620000002", active: true },
      { id: "w3", worker_id: "sim-tat-w3", user_id: "sim-tat-w3", name: "Tomás Gil", email: "tomas@estudiotatuajes.com", phone: "+34620000003", active: true },
    ],
  },
  fisioterapia: {
    label: "Fisioterapia",
    services: [
      { id: "s1",  service_id: "sim-fis-s1",  name: "Sesión de fisioterapia 30min", duration: 30, total_duration: 30, price: 35, color: "#10B981" },
      { id: "s2",  service_id: "sim-fis-s2",  name: "Sesión de fisioterapia 45min", duration: 45, total_duration: 45, price: 45, color: "#059669" },
      { id: "s3",  service_id: "sim-fis-s3",  name: "Sesión de fisioterapia 60min", duration: 60, total_duration: 60, price: 55, color: "#047857" },
      { id: "s4",  service_id: "sim-fis-s4",  name: "Masaje deportivo",             duration: 60, total_duration: 60, price: 55, color: "#3B82F6" },
      { id: "s5",  service_id: "sim-fis-s5",  name: "Masaje descontracturante",     duration: 45, total_duration: 45, price: 45, color: "#2563EB" },
      { id: "s6",  service_id: "sim-fis-s6",  name: "Punción seca",                 duration: 30, total_duration: 30, price: 40, color: "#F59E0B" },
      { id: "s7",  service_id: "sim-fis-s7",  name: "Drenaje linfático",            duration: 60, total_duration: 60, price: 55, color: "#06B6D4" },
      { id: "s8",  service_id: "sim-fis-s8",  name: "Rehabilitación post-lesión",   duration: 60, total_duration: 60, price: 60, color: "#8B5CF6" },
      { id: "s9",  service_id: "sim-fis-s9",  name: "Primera consulta + valoración", duration: 45, total_duration: 45, price: 50, color: "#EC4899" },
      { id: "s10", service_id: "sim-fis-s10", name: "Tratamiento cervical",         duration: 45, total_duration: 45, price: 45, color: "#14B8A6" },
      { id: "s11", service_id: "sim-fis-s11", name: "Tratamiento lumbar",           duration: 45, total_duration: 45, price: 45, color: "#0EA5E9" },
      { id: "s12", service_id: "sim-fis-s12", name: "Vendaje neuromuscular",        duration: 20, total_duration: 20, price: 18, color: "#F97316" },
    ],
    clientNames: [
      "Javier Jiménez", "Carmen García", "David Romero", "Laura Sánchez", "Miguel Torres",
      "Elena Ruiz", "Antonio López", "Sara Moreno", "Pedro González", "Isabel Fernández",
      "Carlos Martínez", "Lucía Navarro", "Francisco Ruiz", "Ana Martínez", "Diego Romero",
    ],
    workers: [
      { id: "w1", worker_id: "sim-fis-w1", user_id: "sim-fis-w1", name: "Marta Gómez", email: "marta@fisioterapia.com", phone: "+34616000001", active: true },
      { id: "w2", worker_id: "sim-fis-w2", user_id: "sim-fis-w2", name: "Luis Hernández", email: "luis@fisioterapia.com", phone: "+34616000002", active: true },
      { id: "w3", worker_id: "sim-fis-w3", user_id: "sim-fis-w3", name: "Pablo Serrano", email: "pablo@fisioterapia.com", phone: "+34616000003", active: true },
    ],
  },
  psicologia: {
    label: "Psicología",
    services: [
      { id: "s1", service_id: "sim-psi-s1", name: "Primera sesión (evaluación)", duration: 60, total_duration: 60, price: 70, color: "#8B5CF6" },
      { id: "s2", service_id: "sim-psi-s2", name: "Sesión individual",           duration: 50, total_duration: 50, price: 60, color: "#7C3AED" },
      { id: "s3", service_id: "sim-psi-s3", name: "Sesión online",               duration: 50, total_duration: 50, price: 55, color: "#6D28D9" },
      { id: "s4", service_id: "sim-psi-s4", name: "Terapia de pareja",           duration: 75, total_duration: 75, price: 90, color: "#EC4899" },
      { id: "s5", service_id: "sim-psi-s5", name: "Terapia familiar",            duration: 75, total_duration: 75, price: 90, color: "#DB2777" },
      { id: "s6", service_id: "sim-psi-s6", name: "Terapia infantil",            duration: 50, total_duration: 50, price: 60, color: "#F59E0B" },
      { id: "s7", service_id: "sim-psi-s7", name: "Terapia adolescentes",        duration: 50, total_duration: 50, price: 60, color: "#D97706" },
      { id: "s8", service_id: "sim-psi-s8", name: "Evaluación neuropsicológica", duration: 90, total_duration: 90, price: 130, color: "#0EA5E9" },
      { id: "s9", service_id: "sim-psi-s9", name: "Informe psicológico",         duration: 45, total_duration: 45, price: 90, color: "#0284C7" },
      { id: "s10", service_id: "sim-psi-s10", name: "Sesión de seguimiento",     duration: 30, total_duration: 30, price: 40, color: "#14B8A6" },
    ],
    clientNames: [
      "Javier Jiménez", "Carmen García", "David Romero", "Laura Sánchez", "Miguel Torres",
      "Elena Ruiz", "Antonio López", "Sara Moreno", "Pedro González", "Isabel Fernández",
      "Carlos Martínez", "Lucía Navarro", "Francisco Ruiz", "Ana Martínez", "Diego Romero",
    ],
    workers: [
      { id: "w1", worker_id: "sim-psi-w1", user_id: "sim-psi-w1", name: "Elena Vidal", email: "elena@consultapsicologia.com", phone: "+34618000001", active: true },
      { id: "w2", worker_id: "sim-psi-w2", user_id: "sim-psi-w2", name: "Sergio Ibáñez", email: "sergio@consultapsicologia.com", phone: "+34618000002", active: true },
      { id: "w3", worker_id: "sim-psi-w3", user_id: "sim-psi-w3", name: "Nuria Alcalá", email: "nuria@consultapsicologia.com", phone: "+34618000003", active: true },
    ],
  },
  osteopatia: {
    label: "Osteopatía",
    services: [
      { id: "s1", service_id: "sim-ost-s1", name: "Primera consulta + valoración", duration: 60, total_duration: 60, price: 60, color: "#14B8A6" },
      { id: "s2", service_id: "sim-ost-s2", name: "Sesión de osteopatía",          duration: 45, total_duration: 45, price: 50, color: "#0D9488" },
      { id: "s3", service_id: "sim-ost-s3", name: "Osteopatía estructural",        duration: 45, total_duration: 45, price: 50, color: "#0F766E" },
      { id: "s4", service_id: "sim-ost-s4", name: "Osteopatía visceral",           duration: 45, total_duration: 45, price: 55, color: "#115E59" },
      { id: "s5", service_id: "sim-ost-s5", name: "Osteopatía craneal",            duration: 45, total_duration: 45, price: 55, color: "#0891B2" },
      { id: "s6", service_id: "sim-ost-s6", name: "Osteopatía pediátrica",         duration: 40, total_duration: 40, price: 55, color: "#F59E0B" },
      { id: "s7", service_id: "sim-ost-s7", name: "Terapia manual",                duration: 60, total_duration: 60, price: 55, color: "#3B82F6" },
      { id: "s8", service_id: "sim-ost-s8", name: "Punción seca",                  duration: 30, total_duration: 30, price: 40, color: "#8B5CF6" },
      { id: "s9", service_id: "sim-ost-s9", name: "Revisión postural",             duration: 30, total_duration: 30, price: 35, color: "#EC4899" },
    ],
    clientNames: [
      "Javier Jiménez", "Carmen García", "David Romero", "Laura Sánchez", "Miguel Torres",
      "Elena Ruiz", "Antonio López", "Sara Moreno", "Pedro González", "Isabel Fernández",
      "Carlos Martínez", "Lucía Navarro", "Francisco Ruiz", "Ana Martínez", "Diego Romero",
    ],
    workers: [
      { id: "w1", worker_id: "sim-ost-w1", user_id: "sim-ost-w1", name: "Marta Gómez", email: "marta@osteopatia.com", phone: "+34619000001", active: true },
      { id: "w2", worker_id: "sim-ost-w2", user_id: "sim-ost-w2", name: "Luis Hernández", email: "luis@osteopatia.com", phone: "+34619000002", active: true },
      { id: "w3", worker_id: "sim-ost-w3", user_id: "sim-ost-w3", name: "Pablo Serrano", email: "pablo@osteopatia.com", phone: "+34619000003", active: true },
    ],
  },
  salud: {
    label: "Salud / Clínica",
    services: [
      { id: "s1", service_id: "sim-sal-s1", name: "Consulta General", duration: 30, total_duration: 30, price: 60, color: "#3B82F6" },
      { id: "s2", service_id: "sim-sal-s2", name: "Revisión Anual", duration: 45, total_duration: 45, price: 80, color: "#10B981" },
      { id: "s3", service_id: "sim-sal-s3", name: "Extracción de Sangre", duration: 15, total_duration: 15, price: 25, color: "#EF4444" },
      { id: "s4", service_id: "sim-sal-s4", name: "ECG", duration: 20, total_duration: 20, price: 40, color: "#F59E0B" },
    ],
    clientNames: ["Antonio Pérez", "Carmen Molina", "José Luis García", "María Fernández", "Francisco Ruiz",
      "Isabel Díaz", "Manuel López", "Ana Martínez", "Pedro Sánchez", "Dolores González",
      "Javier Hernández", "Pilar Torres", "Carlos Moreno", "Rosa Jiménez", "Andrés Alonso"],
    workers: [
      { id: "w1", worker_id: "sim-sal-w1", user_id: "sim-sal-w1", name: "Dr. Antonio Pérez", email: "antoniop@clinica.com", phone: "+34614000001", active: true },
      { id: "w2", worker_id: "sim-sal-w2", user_id: "sim-sal-w2", name: "Dra. Carmen Molina", email: "carmena@clinica.com", phone: "+34614000002", active: true },
      { id: "w3", worker_id: "sim-sal-w3", user_id: "sim-sal-w3", name: "Dr. Javier Ortega", email: "javiero@clinica.com", phone: "+34614000003", active: true },
    ],
  },
  restaurante: {
    label: "Restaurante",
    services: [
      { id: "s1", service_id: "sim-res-s1", name: "Mesa 2 personas", duration: 90, total_duration: 90, price: 0, color: "#F59E0B" },
      { id: "s2", service_id: "sim-res-s2", name: "Mesa 4 personas", duration: 90, total_duration: 90, price: 0, color: "#10B981" },
      { id: "s3", service_id: "sim-res-s3", name: "Mesa 6+ personas", duration: 120, total_duration: 120, price: 0, color: "#3B82F6" },
      { id: "s4", service_id: "sim-res-s4", name: "Menú Degustación", duration: 150, total_duration: 150, price: 85, color: "#8B5CF6" },
    ],
    clientNames: ["Roberto García", "Sara Jiménez", "Pedro Moreno", "Lucía Navarro", "Diego Romero",
      "Elena Blanco", "Alejandro Castro", "Natalia Vega", "Marcos Ramos", "Julia Torres",
      "Ignacio López", "Cristina Martín", "Rafael Sánchez", "Verónica Díaz", "Emilio González"],
    workers: [
      { id: "w1", worker_id: "sim-res-w1", user_id: "sim-res-w1", name: "Chef David Blanco", email: "david@restaurante.com", phone: "+34615000001", active: true },
      { id: "w2", worker_id: "sim-res-w2", user_id: "sim-res-w2", name: "Sara Jiménez", email: "sara@restaurante.com", phone: "+34615000002", active: true },
      { id: "w3", worker_id: "sim-res-w3", user_id: "sim-res-w3", name: "Marcos Herrera", email: "marcos@restaurante.com", phone: "+34615000003", active: true },
    ],
  },
  lavadero: {
    label: "Lavadero",
    services: [
      { id: "sim-lav-pack1-pequeno", service_id: "sim-lav-pack1-pequeno", name: "Opción 1 - Pequeño", description: "Lavado exterior a mano: jabón, secado y limpieza de llantas.", duration: 45,  total_duration: 45,  price: 46,  car_size: "pequeño", color: "#3B82F6", gender: "unisex" },
      { id: "sim-lav-pack1-mediano", service_id: "sim-lav-pack1-mediano", name: "Opción 1 - Mediano", description: "Lavado exterior a mano: jabón, secado y limpieza de llantas.", duration: 60,  total_duration: 60,  price: 55,  car_size: "mediano", color: "#3B82F6", gender: "unisex" },
      { id: "sim-lav-pack1-grande",  service_id: "sim-lav-pack1-grande",  name: "Opción 1 - Grande",  description: "Lavado exterior a mano: jabón, secado y limpieza de llantas.", duration: 75,  total_duration: 75,  price: 65,  car_size: "grande",  color: "#3B82F6", gender: "unisex" },
      { id: "sim-lav-pack2-pequeno", service_id: "sim-lav-pack2-pequeno", name: "Opción 2 - Pequeño", description: "Lavado exterior + interior: aspirado, salpicadero y cristales.", duration: 90,  total_duration: 90,  price: 75,  car_size: "pequeño", color: "#10B981", gender: "unisex" },
      { id: "sim-lav-pack2-mediano", service_id: "sim-lav-pack2-mediano", name: "Opción 2 - Mediano", description: "Lavado exterior + interior: aspirado, salpicadero y cristales.", duration: 105, total_duration: 105, price: 83,  car_size: "mediano", color: "#10B981", gender: "unisex" },
      { id: "sim-lav-pack2-grande",  service_id: "sim-lav-pack2-grande",  name: "Opción 2 - Grande",  description: "Lavado exterior + interior: aspirado, salpicadero y cristales.", duration: 120, total_duration: 120, price: 91,  car_size: "grande",  color: "#10B981", gender: "unisex" },
      { id: "sim-lav-pack3-pequeno", service_id: "sim-lav-pack3-pequeno", name: "Opción 3 - Pequeño", description: "Lavado completo con cera protectora y limpieza de tapicería.", duration: 120, total_duration: 120, price: 109, car_size: "pequeño", color: "#F59E0B", gender: "unisex" },
      { id: "sim-lav-pack3-mediano", service_id: "sim-lav-pack3-mediano", name: "Opción 3 - Mediano", description: "Lavado completo con cera protectora y limpieza de tapicería.", duration: 135, total_duration: 135, price: 119, car_size: "mediano", color: "#F59E0B", gender: "unisex" },
      { id: "sim-lav-pack3-grande",  service_id: "sim-lav-pack3-grande",  name: "Opción 3 - Grande",  description: "Lavado completo con cera protectora y limpieza de tapicería.", duration: 150, total_duration: 150, price: 129, car_size: "grande",  color: "#F59E0B", gender: "unisex" },
      { id: "sim-lav-pack4-pequeno", service_id: "sim-lav-pack4-pequeno", name: "Opción 4 - Pequeño", description: "Detallado premium: pulido, encerado y limpieza interior profunda.", duration: 150, total_duration: 150, price: 129, car_size: "pequeño", color: "#8B5CF6", gender: "unisex" },
      { id: "sim-lav-pack4-mediano", service_id: "sim-lav-pack4-mediano", name: "Opción 4 - Mediano", description: "Detallado premium: pulido, encerado y limpieza interior profunda.", duration: 165, total_duration: 165, price: 139, car_size: "mediano", color: "#8B5CF6", gender: "unisex" },
      { id: "sim-lav-pack4-grande",  service_id: "sim-lav-pack4-grande",  name: "Opción 4 - Grande",  description: "Detallado premium: pulido, encerado y limpieza interior profunda.", duration: 180, total_duration: 180, price: 149, car_size: "grande",  color: "#8B5CF6", gender: "unisex" },
      { id: "sim-lav-pack1-moto",    service_id: "sim-lav-pack1-moto",    name: "Opción 1 - Moto",    description: "Lavado exterior a mano: chasis, ruedas y secado.",                  duration: 35,  total_duration: 35,  price: 35,  car_size: "moto",    color: "#F97316", gender: "unisex" },
      { id: "sim-lav-pack2-moto",    service_id: "sim-lav-pack2-moto",    name: "Opción 2 - Moto",    description: "Lavado completo con desengrasado de cadena y carenado.",         duration: 60,  total_duration: 60,  price: 55,  car_size: "moto",    color: "#F97316", gender: "unisex" },
      { id: "sim-lav-pack3-moto",    service_id: "sim-lav-pack3-moto",    name: "Opción 3 - Moto",    description: "Lavado premium con encerado y abrillantado de plásticos.",      duration: 80,  total_duration: 80,  price: 79,  car_size: "moto",    color: "#F97316", gender: "unisex" },
      { id: "sim-lav-pack4-moto",    service_id: "sim-lav-pack4-moto",    name: "Opción 4 - Moto",    description: "Detallado completo: pulido, encerado y protección duradera.",   duration: 100, total_duration: 100, price: 99,  car_size: "moto",    color: "#F97316", gender: "unisex" },
    ],
    workers: [
      { id: "sim-lav-w1", worker_id: "sim-lav-w1", user_id: "sim-lav-w1", name: "Alejandro García", email: "alejandro@lavadero.es", phone: "+34611000001", active: true },
      { id: "sim-lav-w2", worker_id: "sim-lav-w2", user_id: "sim-lav-w2", name: "Carlos Martínez",  email: "carlos@lavadero.es",    phone: "+34611000002", active: true },
      { id: "sim-lav-w3", worker_id: "sim-lav-w3", user_id: "sim-lav-w3", name: "Miguel Torres",    email: "miguel@lavadero.es",    phone: "+34611000003", active: true },
    ],
    clientNames: [
      "Javier Jiménez", "Carmen García", "David Romero", "Laura Sánchez", "Miguel Torres",
      "Elena Ruiz", "Antonio López", "Sara Moreno", "Pedro González", "Isabel Fernández",
    ],
  },
  taller: {
    label: "Taller Mecánico",
    services: [
      { id: "s1", service_id: "sim-tal-s1", name: "Revisión ITV", duration: 60, total_duration: 60, price: 45, color: "#EF4444" },
      { id: "s2", service_id: "sim-tal-s2", name: "Cambio de Aceite", duration: 30, total_duration: 30, price: 60, color: "#F59E0B" },
      { id: "s3", service_id: "sim-tal-s3", name: "Cambio de Neumáticos", duration: 45, total_duration: 45, price: 80, color: "#10B981" },
      { id: "s4", service_id: "sim-tal-s4", name: "Diagnóstico General", duration: 60, total_duration: 60, price: 35, color: "#3B82F6" },
    ],
    clientNames: ["Roberto Delgado", "Francisco Navarro", "Álvaro Herrera", "Gonzalo Campos", "Rodrigo Vargas",
      "Emilio Mendoza", "Héctor Guerrero", "Rubén Castillo", "Bernardo Medina", "Guillermo Reyes",
      "Armando Silva", "Nicolás Morales", "Sebastián Flores", "Daniel Castro", "Tomás Jiménez"],
    workers: [
      { id: "w1", worker_id: "sim-tal-w1", user_id: "sim-tal-w1", name: "Roberto Delgado", email: "roberto@taller.com", phone: "+34616000001", active: true },
      { id: "w2", worker_id: "sim-tal-w2", user_id: "sim-tal-w2", name: "Francisco Navarro", email: "francisco@taller.com", phone: "+34616000002", active: true },
      { id: "w3", worker_id: "sim-tal-w3", user_id: "sim-tal-w3", name: "Antonio Gil", email: "antonio@taller.com", phone: "+34616000003", active: true },
    ],
  },
  despacho_abogados: {
    label: "Despacho de Abogados",
    services: [
      { id: "s1",  service_id: "sim-dab-s1",  name: "Consulta legal inicial",           duration: 30,  total_duration: 30,  price: 100, color: "#4F46E5" },
      { id: "s2",  service_id: "sim-dab-s2",  name: "Asesoría jurídica",                duration: 45,  total_duration: 45,  price: 150, color: "#4338CA" },
      { id: "s3",  service_id: "sim-dab-s3",  name: "Revisión de documentos",           duration: 60,  total_duration: 60,  price: 180, color: "#3730A3" },
      { id: "s4",  service_id: "sim-dab-s4",  name: "Redacción de contratos",           duration: 60,  total_duration: 60,  price: 200, color: "#312E81" },
      { id: "s5",  service_id: "sim-dab-s5",  name: "Constitución de sociedad",         duration: 60,  total_duration: 60,  price: 350, color: "#06B6D4" },
      { id: "s6",  service_id: "sim-dab-s6",  name: "Asuntos laborales",                duration: 45,  total_duration: 45,  price: 140, color: "#059669" },
      { id: "s7",  service_id: "sim-dab-s7",  name: "Asuntos de familia",               duration: 60,  total_duration: 60,  price: 180, color: "#DC2626" },
      { id: "s8",  service_id: "sim-dab-s8",  name: "Herencias y testamentos",          duration: 60,  total_duration: 60,  price: 200, color: "#B91C1C" },
      { id: "s9",  service_id: "sim-dab-s9",  name: "Derecho inmobiliario",             duration: 60,  total_duration: 60,  price: 180, color: "#0891B2" },
      { id: "s10", service_id: "sim-dab-s10", name: "Demanda/litigio",                  duration: 90,  total_duration: 90,  price: 300, color: "#EA580C" },
      { id: "s11", service_id: "sim-dab-s11", name: "Representación en juicio",         duration: 120, total_duration: 120, price: 400, color: "#C2410C" },
      { id: "s12", service_id: "sim-dab-s12", name: "Defensa penal",                    duration: 90,  total_duration: 90,  price: 350, color: "#9333EA" },
      { id: "s13", service_id: "sim-dab-s13", name: "Seguimiento de asunto",            duration: 30,  total_duration: 30,  price: 80,  color: "#64748B" },
      { id: "s14", service_id: "sim-dab-s14", name: "Reunión de coordinación",          duration: 60,  total_duration: 60,  price: 140, color: "#475569" },
      { id: "s15", service_id: "sim-dab-s15", name: "Consulta telefónica",              duration: 15,  total_duration: 15,  price: 40,  color: "#334155" },
    ],
    clientNames: ["María González", "José Luis Fernández", "Carmen Rodríguez", "Antonio Pérez", "Isabel López",
      "Francisco García", "Ana Martínez", "Pedro Sánchez", "Magdalena Torres", "Manuel Díaz",
      "Josefina Hernández", "Carlos Moreno", "Teresa Jiménez", "Diego Ruiz", "Esperanza Flores"],
    workers: [
      { id: "w1", worker_id: "sim-dab-w1", user_id: "sim-dab-w1", name: "Lic. María González", email: "maria@despacho.com", phone: "+34617000001", active: true },
      { id: "w2", worker_id: "sim-dab-w2", user_id: "sim-dab-w2", name: "Lic. José Luis Fernández", email: "joseluis@despacho.com", phone: "+34617000002", active: true },
      { id: "w3", worker_id: "sim-dab-w3", user_id: "sim-dab-w3", name: "Lic. Elena Navarro", email: "elena@despacho.com", phone: "+34617000003", active: true },
    ],
  },
  comida_para_llevar: {
    label: "Comida para llevar",
    services: [],  // food-orders does not use appointment services
    workers: [
      { id: "w1", worker_id: "sim-food-w1", user_id: "sim-food-w1", name: "María Gómez", email: "maria@comidaparallevar.com", phone: "+34612000001", active: true },
      { id: "w2", worker_id: "sim-food-w2", user_id: "sim-food-w2", name: "Antonio Pérez", email: "antonio@comidaparallevar.com", phone: "+34612000002", active: true },
    ],
    clientNames: [
      "Carlos Ruiz", "María López", "Ana García", "Pedro Martínez",
      "Isabel Sánchez", "Roberto Fernández", "Laura Jiménez", "Diego Torres",
    ],
  },
  restaurantes: {
    label: "Restaurantes",
    services: [],  // food-orders does not use appointment services
    workers: [
      { id: "w1", worker_id: "sim-rest-w1", user_id: "sim-rest-w1", name: "Lucía Fernández", email: "lucia@restaurante.com", phone: "+34612000003", active: true },
      { id: "w2", worker_id: "sim-rest-w2", user_id: "sim-rest-w2", name: "Javier Romero", email: "javier@restaurante.com", phone: "+34612000004", active: true },
    ],
    clientNames: [
      "Carlos Ruiz", "María López", "Ana García", "Pedro Martínez",
      "Isabel Sánchez", "Roberto Fernández", "Laura Jiménez", "Diego Torres",
    ],
  },
  autoescuela: {
    label: "Autoescuela",
    services: [],
    workers: [],
    clientNames: [
      "Alejandro García", "Sofía Martínez", "Hugo Fernández", "Lucía Rodríguez",
      "Pablo Sánchez", "Marta López", "Daniel Hernández", "Carmen Torres",
      "Adrián Ruiz", "Elena Moreno", "Javier Díaz", "Natalia Jiménez",
      "Sergio González", "Irene Castro", "Álvaro Romero",
    ],
    // CAP relativo a hoy (la demo nunca caduca): +40 días = chip ámbar + banner; +200 = verde.
    mockAlumnos: [
      { alumno_id: "alu-001", instructor_id: "inst-001", name: "Alejandro García", status: "activo", phone: "+34611100001", dni: "12345678A", carnet_type: "B", horas_acumuladas: 12, practicas_completadas: 8, fecha_nacimiento: "2004-03-15", observaciones: "Progresa bien, mejorar incorporaciones", cap: capDemo(40), caducidades: { dni: fechaDemo(900), medico: fechaDemo(18), expediente: fechaDemo(400) } },
      { alumno_id: "alu-002", instructor_id: "inst-002", name: "Sofía Martínez", status: "activo", phone: "+34611100002", dni: "23456789B", carnet_type: "B", horas_acumuladas: 6, practicas_completadas: 4, fecha_nacimiento: "2005-07-22", observaciones: "Necesita refuerzo en rotondas", cambio: "automatico" /* coche de cada alumno (14/10/2026): va en el Toyota automático */ },
      { alumno_id: "alu-003", instructor_id: "inst-001", name: "Hugo Fernández", status: "aprobado", phone: "+34611100003", dni: "34567890C", carnet_type: "B", horas_acumuladas: 20, practicas_completadas: 16, fecha_nacimiento: "2003-11-08", observaciones: "Aprobó a la primera" },
      { alumno_id: "alu-004", instructor_id: "inst-003", name: "Lucía Rodríguez", status: "activo", phone: "+34611100004", dni: "45678901D", carnet_type: "A2", horas_acumuladas: 3, practicas_completadas: 2, fecha_nacimiento: "2000-01-30", observaciones: "", caducidades: { expediente: fechaDemo(25) } },
      { alumno_id: "alu-005", instructor_id: "inst-002", name: "Pablo Sánchez", status: "inactivo", phone: "+34611100005", dni: "56789012E", carnet_type: "B", horas_acumuladas: 8, practicas_completadas: 5, fecha_nacimiento: "2004-09-12", observaciones: "Pausó las clases temporalmente" },
      { alumno_id: "alu-006", instructor_id: "inst-003", name: "Marta López", status: "activo", phone: "+34611100006", dni: "67890123F", carnet_type: "B", horas_acumuladas: 15, practicas_completadas: 11, fecha_nacimiento: "2005-05-04", observaciones: "Preparando examen práctico", cap: capDemo(200), caducidades: { dni: fechaDemo(-4), medico: fechaDemo(300) } },
      // Sin teléfono a propósito: en «Enviar semana» y «Planificar semana» sale el aviso «sin teléfono» con
      // el enlace a su ficha para añadirlo (como en la app real).
      { alumno_id: "alu-007", name: "Raúl Ortega", status: "activo", dni: "78901234G", carnet_type: "B", horas_acumuladas: 2, practicas_completadas: 1, fecha_nacimiento: "2006-02-17", observaciones: "Se matriculó en la oficina; falta su móvil" },
      // Alumno de prueba de David, con su móvil REAL: «Notificar de forma individual» y «Enviar semana» le
      // mandan el WhatsApp de verdad a este número, sin preguntar el móvil del lead. Siempre está (no se
      // borra al salir del simulador). Espejo de DAVID_ALUMNO en pages/autoescuela/simAlumnosStore.js.
      { alumno_id: "alu-david-prueba", instructor_id: "inst-001", name: "David (prueba Conext)", status: "activo", phone: "+34686851695", dni: "", carnet_type: "B", horas_acumuladas: 0, practicas_completadas: 0, fecha_nacimiento: "", observaciones: "Alumno de prueba de Conext: los avisos le llegan de verdad a este móvil.", sim_real_phone: true },
      // Semana llena (9/10/2026): 6 alumnos más por profesor para que cada uno tenga la semana llena (semanaLlenaDemo.js).
      ...alumnosSemanaLlena(),
    ],
    mockPracticas: (() => {
      const d = dayjs;
      const base = d().startOf("week").add(1, "day"); // lunes
      const lista = [
        // Clases teóricas pasadas (completadas)
        { practica_id: "prac-clase-001", alumno_ids: ["alu-001"], type: "clase", start: base.subtract(10, "day").hour(18).minute(0).toISOString(), end: base.subtract(10, "day").hour(19).minute(30).toISOString(), status: "completada", instructor_id: "inst-001", notas: "Clase teórica completada" },
        { practica_id: "prac-clase-002", alumno_ids: ["alu-001"], type: "clase", start: base.subtract(5, "day").hour(18).minute(0).toISOString(), end: base.subtract(5, "day").hour(19).minute(30).toISOString(), status: "completada", instructor_id: "inst-002", notas: "Clase teórica completada" },
        { practica_id: "prac-clase-003", alumno_ids: ["alu-002"], type: "clase", start: base.subtract(7, "day").hour(17).minute(0).toISOString(), end: base.subtract(7, "day").hour(18).minute(30).toISOString(), status: "completada", instructor_id: "inst-003", notas: "Clase teórica completada" },
        // Lunes
        { practica_id: "pr-001", alumno_ids: ["alu-001"], type: "ciudad", start: base.hour(9).minute(0).toISOString(), end: base.hour(10).minute(0).toISOString(), status: "pendiente", instructor_id: "inst-001", notas: "Progresa bien" },
        { practica_id: "pr-002", alumno_ids: ["alu-002"], type: "ciudad", start: base.hour(10).minute(30).toISOString(), end: base.hour(11).minute(30).toISOString(), status: "pendiente", instructor_id: "inst-001", notas: "" },
        { practica_id: "pr-009", alumno_ids: ["alu-006"], type: "carretera", start: base.hour(16).minute(0).toISOString(), end: base.hour(17).minute(30).toISOString(), status: "pendiente", instructor_id: "inst-002", notas: "Preparando examen" },
        // Martes
        { practica_id: "pr-003", alumno_ids: ["alu-001", "alu-006"], type: "carretera", start: base.add(1, "day").hour(9).minute(0).toISOString(), end: base.add(1, "day").hour(10).minute(30).toISOString(), status: "pendiente", instructor_id: "inst-002", notas: "" },
        { practica_id: "pr-004", alumno_ids: ["alu-004"], type: "autovia", start: base.add(1, "day").hour(11).minute(0).toISOString(), end: base.add(1, "day").hour(12).minute(0).toISOString(), status: "pendiente", instructor_id: "inst-001", notas: "" },
        { practica_id: "pr-010", alumno_ids: ["alu-002", "alu-004"], type: "ciudad", start: base.add(1, "day").hour(16).minute(30).toISOString(), end: base.add(1, "day").hour(17).minute(30).toISOString(), status: "pendiente", instructor_id: "inst-003", notas: "" },
        // Miércoles
        { practica_id: "pr-005", alumno_ids: ["alu-006"], type: "examen", start: base.add(2, "day").hour(8).minute(30).toISOString(), end: base.add(2, "day").hour(9).minute(30).toISOString(), status: "pendiente", instructor_id: "inst-003", notas: "Examen práctico DGT" },
        { practica_id: "pr-006", alumno_ids: ["alu-002"], type: "ciudad", start: base.add(2, "day").hour(10).minute(0).toISOString(), end: base.add(2, "day").hour(11).minute(0).toISOString(), status: "cancelada", motivo_cancelacion: "Alumna enferma", instructor_id: "inst-002", notas: "Alumna canceló por enfermedad" },
        { practica_id: "pr-011", alumno_ids: ["alu-001"], type: "autovia", start: base.add(2, "day").hour(15).minute(30).toISOString(), end: base.add(2, "day").hour(16).minute(30).toISOString(), status: "pendiente", instructor_id: "inst-001", notas: "" },
        // Jueves
        { practica_id: "pr-007", alumno_ids: ["alu-001"], type: "carretera", start: base.add(3, "day").hour(9).minute(0).toISOString(), end: base.add(3, "day").hour(10).minute(30).toISOString(), status: "pendiente", instructor_id: "inst-001", notas: "" },
        { practica_id: "pr-012", alumno_ids: ["alu-001"], type: "ciudad", start: base.add(3, "day").hour(10).minute(30).toISOString(), end: base.add(3, "day").hour(11).minute(30).toISOString(), status: "pendiente", instructor_id: "inst-001", notas: "" },
        { practica_id: "pr-013", alumno_ids: ["alu-006", "alu-002"], type: "carretera", start: base.add(3, "day").hour(16).minute(0).toISOString(), end: base.add(3, "day").hour(17).minute(30).toISOString(), status: "pendiente", instructor_id: "inst-002", notas: "" },
        // Cancelada cuyo hueco se volvió a reservar (pr-013): queda DEBAJO de la práctica viva, como una tira clicable en el borde.
        { practica_id: "pr-018", alumno_ids: ["alu-004"], type: "ciudad", start: base.add(3, "day").hour(16).minute(0).toISOString(), end: base.add(3, "day").hour(17).minute(0).toISOString(), status: "cancelada", motivo_cancelacion: "Cambio de horario del alumno", instructor_id: "inst-002", notas: "Canceló y el hueco se ofreció a otros alumnos" },
        // Viernes
        { practica_id: "pr-008", alumno_ids: ["alu-004", "alu-002"], type: "ciudad", start: base.add(4, "day").hour(9).minute(0).toISOString(), end: base.add(4, "day").hour(10).minute(0).toISOString(), status: "pendiente", instructor_id: "inst-003", notas: "" },
        { practica_id: "pr-014", alumno_ids: ["alu-001"], type: "examen", start: base.add(4, "day").hour(11).minute(0).toISOString(), end: base.add(4, "day").hour(12).minute(0).toISOString(), status: "pendiente", instructor_id: "inst-003", notas: "Examen final carnet B" },
        // Reservada por la alumna por WhatsApp con «el tipo lo decide el profesor»: queda «Por decidir».
        { practica_id: "pr-017", alumno_ids: ["alu-002"], type: "por_definir", start: base.add(4, "day").hour(17).minute(0).toISOString(), end: base.add(4, "day").hour(18).minute(0).toISOString(), status: "pendiente", instructor_id: "inst-002", notas: "Reservada por la alumna por WhatsApp" },
        // Sábado
        { practica_id: "pr-015", alumno_ids: ["alu-006"], type: "ciudad", start: base.add(5, "day").hour(9).minute(0).toISOString(), end: base.add(5, "day").hour(10).minute(0).toISOString(), status: "pendiente", instructor_id: "inst-003", notas: "" },
        { practica_id: "pr-016", alumno_ids: ["alu-004"], type: "ciudad", start: base.add(5, "day").hour(10).minute(30).toISOString(), end: base.add(5, "day").hour(11).minute(30).toISOString(), status: "pendiente", instructor_id: "inst-003", notas: "Primera práctica A2" },
        // ── Añadidas AL FINAL: el estado de aviso de practicasDemo.js va por posición (no mover). ──
        // Alumno sin teléfono: no se le puede avisar por WhatsApp.
        { practica_id: "pr-019", alumno_ids: ["alu-007"], type: "ciudad", start: base.add(3, "day").hour(12).minute(0).toISOString(), end: base.add(3, "day").hour(13).minute(0).toISOString(), status: "pendiente", instructor_id: "inst-002", notas: "" },
        // Histórico de las dos semanas anteriores, ya marcado por el profesor: alimenta el panel
        // «Plantones y coste» del Dashboard (plantones, hueco recuperado, cargo por cancelación tardía).
        { practica_id: "pr-h01", alumno_ids: ["alu-002"], type: "ciudad", start: base.subtract(7, "day").hour(10).minute(0).toISOString(), end: base.subtract(7, "day").hour(11).minute(0).toISOString(), status: "completada", confirmed: true, instructor_id: "inst-001", notas: "" },
        { practica_id: "pr-h02", alumno_ids: ["alu-005"], type: "carretera", start: base.subtract(6, "day").hour(9).minute(0).toISOString(), end: base.subtract(6, "day").hour(10).minute(30).toISOString(), status: "no_asistio", instructor_id: "inst-002", notas: "No se presentó ni avisó" },
        { practica_id: "pr-h03", alumno_ids: ["alu-006"], type: "ciudad", start: base.subtract(5, "day").hour(11).minute(0).toISOString(), end: base.subtract(5, "day").hour(12).minute(0).toISOString(), status: "completada", confirmed: true, creation_source: "slot_fill", instructor_id: "inst-003", notas: "Hueco recuperado: se ofreció por WhatsApp tras una cancelación" },
        { practica_id: "pr-h04", alumno_ids: ["alu-004"], type: "autovia", start: base.subtract(4, "day").hour(16).minute(0).toISOString(), end: base.subtract(4, "day").hour(17).minute(0).toISOString(), status: "cancelada", motivo_cancelacion: "Canceló 3 horas antes", cancelada_por: "alumno", cancel_origin: "whatsapp", late_cancellation: { applies: true, applied: true, movimiento_id: "mov-late-001", amount_cents: 2500, origin: "whatsapp" }, instructor_id: "inst-001", notas: "Cancelación tardía: se cobró la práctica" },
        { practica_id: "pr-h05", alumno_ids: ["alu-001"], type: "ciudad", start: base.subtract(3, "day").hour(9).minute(0).toISOString(), end: base.subtract(3, "day").hour(10).minute(0).toISOString(), status: "completada", confirmed: true, instructor_id: "inst-001", notas: "" },
        { practica_id: "pr-h06", alumno_ids: ["alu-005"], type: "ciudad", start: base.subtract(11, "day").hour(17).minute(0).toISOString(), end: base.subtract(11, "day").hour(18).minute(0).toISOString(), status: "no_asistio", instructor_id: "inst-002", notas: "Segundo plantón" },
        { practica_id: "pr-h07", alumno_ids: ["alu-006"], type: "autovia", start: base.subtract(12, "day").hour(10).minute(0).toISOString(), end: base.subtract(12, "day").hour(11).minute(0).toISOString(), status: "completada", instructor_id: "inst-002", notas: "" },
        { practica_id: "pr-h08", alumno_ids: ["alu-002"], type: "carretera", start: base.subtract(10, "day").hour(16).minute(0).toISOString(), end: base.subtract(10, "day").hour(17).minute(30).toISOString(), status: "completada", confirmed: true, instructor_id: "inst-003", notas: "" },
        // Cancelada A MANO a petición del alumno (llamó por teléfono): misma política que el botón; el hueco se recuperó, sin cargo.
        { practica_id: "pr-h09", alumno_ids: ["alu-002"], type: "ciudad", start: base.subtract(9, "day").hour(12).minute(0).toISOString(), end: base.subtract(9, "day").hour(13).minute(0).toISOString(), status: "cancelada", cancelada_por: "alumno", cancel_origin: "manual", refilled_by_practica_id: "pr-h03", late_cancellation: { applies: true, applied: false, movimiento_id: null, waived_reason: "slot_refilled", amount_cents: 2500, origin: "manual" }, instructor_id: "inst-003", notas: "Llamó para cancelar; el hueco se lo quedó otro alumno" },
      ];
      // Semana llena (9/10/2026, para las demos): de 4 a 6 prácticas al día por profesor de lunes a viernes, con
      // alumnos nuevos, circuitos cerrados y alguna cancelada. AL FINAL de la lista (practicasDemo.js va por posición).
      lista.push(...practicasSemanaLlena(base, lista));
      // Historial trazable (como la app real, 2/10/2026): cada práctica de conducción lleva el coche
      // habitual de su profesor (flotaStore: inst-001 → veh-001…) y las ya hechas, por dónde fueron y
      // cómo se evaluó. Se añade aquí y no en cada fila para no tocar el orden (practicasDemo.js va por posición).
      const COCHE_DE = { "inst-001": "veh-001", "inst-002": "veh-002", "inst-003": "veh-003" };
      const TRAZA = {
        "pr-h01": { recorrido: "Centro, avenida del Puerto y rotondas de la ronda norte", evaluacion: { items: [{ id: "ciudad", estado: "bien" }, { id: "maniobras", estado: "necesita_mejorar" }], nota: "Le cuesta el embrague en las cuestas." } },
        "pr-h03": { recorrido: "Barrio de San Blas y aparcamiento del mercado", evaluacion: { items: [{ id: "ciudad", estado: "dominado" }, { id: "aparcamiento", estado: "bien" }], nota: "" } },
        "pr-h05": { recorrido: "Casco urbano, colegios (zona 30) y glorieta de la estación", evaluacion: { items: [{ id: "ciudad", estado: "bien" }, { id: "maniobras", estado: "bien" }], nota: "Mejora mucho en las incorporaciones." } },
        "pr-h07": { recorrido: "A-7 entre las salidas 12 y 18, incorporación y cambio de carril", evaluacion: { items: [{ id: "autovia", estado: "dominado" }], nota: "Lista para el examen en este tramo." } },
        "pr-h08": { recorrido: "Carretera CV-300 hasta el pueblo y vuelta por la nacional", evaluacion: { items: [{ id: "carretera", estado: "bien" }, { id: "maniobras", estado: "necesita_mejorar" }], nota: "Adelantamientos: mirar antes el retrovisor." } },
      };
      return lista.map((p) => ({
        ...p,
        ...(p.type !== "clase" && !p.vehicle_id && COCHE_DE[p.instructor_id] ? { vehicle_id: COCHE_DE[p.instructor_id] } : {}),
        ...(TRAZA[p.practica_id] && TRAZA[p.practica_id].recorrido ? { recorrido: TRAZA[p.practica_id].recorrido } : {}),
        ...(TRAZA[p.practica_id] && TRAZA[p.practica_id].evaluacion ? { evaluacion: { ...TRAZA[p.practica_id].evaluacion, at: p.end } } : {}),
      }));
    })(),
    mockInstructors: [
      { instructor_id: "inst-001", name: "Carlos Navarro", phone: "+34622000001", horario: "L-V 8:00-15:00" },
      { instructor_id: "inst-002", name: "Ana Belén Ruiz", phone: "+34622000002", horario: "L-V 9:00-17:00" },
      { instructor_id: "inst-003", name: "Miguel Ángel Torres", phone: "+34622000003", horario: "L-S 8:00-14:00" },
    ],
  },
  bares_deportes: {
    label: "Bares (Deportes)",
    services: [],
    workers: [],
    clientNames: [
      "Carlos Jiménez", "Javier Ruiz", "Antonio García", "Manuel López", "Francisco Sánchez",
      "José Martínez", "David Hernández", "Pedro Torres", "Juan Díaz", "Miguel Romero",
      "Sergio Álvarez", "Raúl Moreno", "Óscar Navarro", "Adrián Gil", "Pablo Delgado",
      "Fernando Ortega", "Diego Castro", "Rubén Molina", "Víctor Medina", "Álvaro Vargas",
      "Roberto Ramos", "Tomás Reyes", "Enrique Blanco", "Andrés Morales", "Jesús Domínguez",
      "Rafael Guerrero", "Ignacio Moya", "Agustín Prieto", "Alberto Pena", "Emilio Lozano",
      "Salvador Rubio", "Ramón Fuentes", "Julián Núñez", "Cristóbal Abad", "Eugenio Vidal",
      "Gonzalo Bernal", "Eduardo Cano", "Lorenzo Serrano", "Valentín Vega", "Patricio Calvo",
      "Guillermo Ibáñez", "Ricardo Herrero", "Leandro Crespo", "Esteban Pascual", "Florencio Marcos",
      "Anabel Torres", "Cristina Suárez", "Vanesa Pardo", "Montserrat León", "Paloma Flores",
    ],
  },
};

const DEFAULT_SECTOR = "barberia";

function getSectorData(sector) {
  let normalized = sector === "peluqueria_unisex" ? "peluquerias" : sector;
  // Distribución reutiliza los mocks de "comida para llevar" (mismo comportamiento por ahora).
  if (normalized === "distribucion") normalized = "comida_para_llevar";
  // Marketing genérico reutiliza los mocks de bares (sin servicios, solo clientes).
  if (normalized === "marketing") normalized = "bares_deportes";
  return SECTOR_DATA[normalized] || SECTOR_DATA[DEFAULT_SECTOR];
}

/**
 * Sectores de food-orders (pedidos): la app gira en torno a PEDIDOS, no a citas.
 * Los chats mock deben hablar de pedidos/recogida/entrega en vez de citas.
 */
function isFoodOrdersSector(sector) {
  return ["pizzeria", "comida_para_llevar", "restaurantes", "distribucion"].includes(
    (sector || "").toLowerCase().trim()
  );
}

/* ─── Helpers para generación de datos ─────────────────────── */

/**
 * Genera un número de teléfono mock para un contacto dado un índice y su nombre.
 * Los contactos estrella (Javier Jiménez, Carmen García) usan el teléfono real
 * del vendedor logueado para que los mensajes de demo lleguen a su dispositivo.
 * David Romero usa un número fijo independiente del vendedor.
 */
function generateMockPhone(index, name) {
  if (name === "Javier Jiménez") {
    return getVendorDemoPhone();
  }
  if (name === "Carmen García") {
    return getVendorDemoPhone();
  }
  if (name === "David Romero") {
    return DAVID_ROMERO_PHONE;
  }
  return `+346${(10000000 + index).toString().slice(1)}`;
}

/* ─── Generadores de mock data ─────────────────────────────── */

function generateMockCalendarEvents(from, to, sector, workersOverride, servicesOverride) {
  const data = getSectorData(sector);
  const events = [];
  
  // Usar trabajadores y servicios proporcionados o los del sector
  const workersToUse = Array.isArray(workersOverride) && workersOverride.length > 0 
    ? workersOverride 
    : data.workers;
    
  const servicesToUse = Array.isArray(servicesOverride) && servicesOverride.length > 0 
    ? servicesOverride 
    : data.services;

  if (workersToUse.length === 0 || servicesToUse.length === 0) return [];

  // Peluquería: día de un salón real con citas intercaladas (tinte/mechas + otra clienta en la exposición).
  if (data === SECTOR_DATA.peluquerias) {
    return buildPeluqueriaAgenda({
      from, to,
      workers: workersToUse,
      services: servicesToUse,
      catalogo: SECTOR_DATA.peluquerias.services,
      clientNames: data.clientNames,
      makePhone: generateMockPhone,
      companyId: SIM_COMPANY_ID,
    });
  }

  // Ampliamos el rango: desde hace 7 días hasta dentro de 14 días
  const startRange = dayjs(from || dayjs().subtract(7, "day")).startOf("day");
  const endRange = dayjs(to || dayjs().add(14, "day")).endOf("day");

  let currentDay = startRange;
  let eventCounter = 0;

  while (currentDay.isBefore(endRange)) {
    const dayOfWeek = currentDay.day(); // 0 = Sunday, 6 = Saturday
    if (dayOfWeek !== 0) { // No hay eventos los domingos
      // Horarios basados en /peluqueria/user-schedule del simulador
      const shifts = dayOfWeek === 6
        ? [{ start: 10, end: 14 }]
        : [{ start: 10, end: 14 }, { start: 16, end: 19 }];

      // Cada trabajador genera sus propios eventos independientemente → solapamientos naturales
      workersToUse.forEach((worker, workerIdx) => {
        const workerId = worker.worker_id || worker.user_id || worker.id;
        const workerName = worker.name || worker.full_name || worker.username || "Staff";

        shifts.forEach((shift) => {
          let hour = shift.start;
          let minute = 0;

          // Entre 2 y 4 citas por turno por trabajador
          const numCitas = Math.floor(Math.random() * 3) + 2;

          for (let i = 0; i < numCitas; i++) {
            if (hour >= shift.end) break;

            const serviceIdx = (eventCounter + workerIdx + i) % servicesToUse.length;
            const clientIdx = (eventCounter + workerIdx + i) % data.clientNames.length;

            const service = servicesToUse[serviceIdx];
            const clientName = data.clientNames[clientIdx];
            const clientPhone = generateMockPhone(clientIdx, clientName);

            const serviceId = service.service_id || service.id;
            const serviceName = service.name || service.nombre;
            const duration = service.duration || service.total_duration || service.duracion_minutos || 30;

            const start = currentDay.hour(hour).minute(minute);
            const end = start.add(duration, "minute");

            // No pasarse del fin del turno
            if (end.hour() > shift.end || (end.hour() === shift.end && end.minute() > 0)) break;

            // Aprox 1 de cada 3 citas mock se muestra como confirmada por el
            // cliente (estable entre re-renders gracias al determinismo de
            // eventCounter, sin Math.random).
            const isConfirmed = eventCounter % 3 === 0;

            // Badge de origen (Google/WhatsApp/web/CRM): variedad determinista
            // por índice para que el equipo comercial pueda hacer demo del badge
            // sin depender del azar (espeja getEventOriginKey de appointments).
            const ORIGIN_CYCLE = ["crm", "whatsapp", "voice", "web", "google"];
            const originKey = ORIGIN_CYCLE[eventCounter % ORIGIN_CYCLE.length];

            events.push({
              event_id: `mock-${start.valueOf()}-${eventCounter}-w${workerIdx}`,
              id: `mock-${start.valueOf()}-${eventCounter}-w${workerIdx}`,
              company_id: SIM_COMPANY_ID,
              title: `${start.format("HH:mm")}-${end.format("HH:mm")}: ${serviceName} - ${workerName}`,
              start: start.toISOString(),
              end: end.toISOString(),
              description: `Cita simulada.\nServicio: ${serviceName}\nProfesional: ${workerName}`,
              color: service.color || "#3B82F6",
              event_type: originKey === "google" ? "external_google" : "manual",
              creation_source: originKey === "whatsapp" || originKey === "web" || originKey === "voice" ? originKey : undefined,
              participants: JSON.stringify([`${SIM_COMPANY_ID}#${clientPhone}`]),
              origin_kind: "booking",
              confirmed: isConfirmed,
              // Algunas las confirmó la IA en la llamada de recordatorio.
              confirmed_via: isConfirmed && eventCounter % 2 === 1 ? "llamada_ia" : undefined,
              service_name: serviceName,
              service_id: serviceId,
              // Sin precio, TODA factura de la demo salía a 0,00 € (el modal usa
              // service_price). El catálogo mock sí lo tiene: se propaga aquí.
              service_price: Number(service.price ?? service.precio ?? 0) || 0,
              client_name: clientName,
              client_phone: clientPhone,
              worker_name: workerName,
              worker_id: workerId,
              resourceId: workerId,
            });

            eventCounter++;
            // Hueco aleatorio entre citas (5 a 20 mins)
            const gap = Math.floor(Math.random() * 15) + 5;
            const nextStart = end.add(gap, "minute");
            hour = nextStart.hour();
            minute = nextStart.minute();
          }
        });
      });
    }
    currentDay = currentDay.add(1, "day");
  }

  return events;
}

export { generateMockCalendarEvents };

function generateMockChats(sector) {
  const data = getSectorData(sector);
  const isFood = isFoodOrdersSector(sector);
  const askSnippet = isFood ? "Hola, ¿puedo hacer un pedido?" : "Hola, ¿podéis darme cita?";
  return data.clientNames.map((name, i) => {
    const phone = generateMockPhone(i, name);
    return {
      company_id: SIM_COMPANY_ID,
      contact_id: `${SIM_COMPANY_ID}#${phone}`,
      nombre: name,
      phone_number: phone,
      snippet: i % 2 === 0 ? askSnippet : "Muchas gracias!",
      snippet_status: i % 3 === 0 ? "received" : "sent",
      last_msg_ts: dayjs().subtract(i * 2, "hour").toISOString(),
      channel: "whatsapp",
      unread_count: i === 0 ? 2 : 0,
    };
  });
}

function generateMockTemplates(sector) {
  const data = getSectorData(sector);
  const sectorLabel = data.label;
  const isBarSector = sector === "bares_deportes" || sector === "bares_tematicos";

  const comunicacionesTemplates = isBarSector
    ? [
        {
          name: "lanzamiento_evento",
          label: "Lanzamiento Evento",
          group_id: "comunicaciones",
          body:
            "¡Hola {{1}}! ⚽🔥\n\nEste domingo el Clásico Barça vs Madrid en *{{2}}*, pantalla gigante y Menú Especial por *28€* (bebida + postre incluidos).\n\n¡No te quedes sin mesa! 📺",
          btn: "Reservar Mesa",
        },
        {
          name: "recordatorio_evento",
          label: "Recordatorio Evento",
          group_id: "comunicaciones",
          body:
            "¡Hola {{1}}! 🍺⚽\n\n¡Mañana el Clásico en *{{2}}*! Menú especial *28€* y quedan muy pocas mesas. ⏳\n\n¿Te guardamos sitio?",
          btn: "Reservar Mesa",
        },
        {
          name: "fiesta_especial",
          label: "Fiesta Especial",
          group_id: "comunicaciones",
          body:
            "¡Hola {{1}}! 🎃\n\nViernes 31 de octubre, 20h — Fiesta Halloween en *{{2}}* con platos especiales para la ocasión. Bebida y postre incluidos por *28€*.\n\n¡Esperamos sorprenderos! 👻",
          btn: "Me apunto 👻",
        },
      ]
    : [
        {
          name: "recordatorio_cita",
          label: "Recordatorio de Cita",
          group_id: "comunicaciones",
          body: "Hola {{1}}, te recordamos tu cita para *{{2}}* en *{{3}}* el día {{4}} a las {{5}}. ¡Te esperamos! ✂️",
          btn: "Confirmar asistencia",
        },
        {
          name: "bienvenida_cliente",
          label: "Bienvenida Cliente",
          group_id: "comunicaciones",
          body: "¡Bienvenid@ a *{{2}}*, {{1}}! 💈 Gracias por confiar en nosotros. Escríbenos para cualquier duda o para pedir tu próxima cita.",
          btn: "Ver servicios",
        },
        {
          name: "recordatorio_reenganche",
          label: "Recordatorio Reenganche",
          group_id: "comunicaciones",
          body: "¡Hola {{1}}! 👋\n\n¿Lista para tu próxim@ *{{2}}*? 🗓️\n\n¡Escríbenos y te buscamos el mejor horario para ti!",
          btn: "🚀 ¡Quiero cita!",
        },
      ];

  const templates = [
    ...comunicacionesTemplates,
    {
      name: "oferta_estacional",
      label: "Oferta Especial",
      group_id: "promociones",
      body: "¡Hola {{1}}! ☀️ Este mes en *{{2}}* tenemos un 20% de descuento en tratamientos de hidratación. ¡Reserva ya tu hueco!",
      btn: "Reservar oferta"
    },
    {
      name: "feliz_cumpleanos",
      label: "Feliz Cumpleaños",
      group_id: "promociones",
      body: "¡Feliz cumpleaños {{1}}! 🎂 En *{{2}}* queremos celebrarlo contigo: tienes un servicio de lavado y peinado de regalo esta semana. ¡Disfruta tu día!",
      btn: "🎁 Reclamar regalo"
    },
    {
      name: "aviso_festivo",
      label: "Aviso Festivo",
      group_id: "avisos",
      body: "Aviso de *{{2}}*: El próximo lunes {{1}} cerraremos por festivo local. ¡Volvemos el martes con más fuerza! 💪",
      btn: "Ver calendario"
    },
  ];

  const enriched = templates.map((t, idx) => {
    const buttons = [{ type: "QUICK_REPLY", text: t.btn }];

    return {
      id: `mock-template-${idx}`,
      name: t.name,
      language: "es",
      status: "APPROVED",
      category: t.group_id === "promociones" ? "MARKETING" : "UTILITY",
      components: [
        { type: "HEADER", format: "TEXT", text: t.label },
        {
          type: "BODY",
          text: t.body,
        },
        { type: "FOOTER", text: sectorLabel },
        { type: "BUTTONS", buttons },
      ],
      extras: t.group_id ? { group_id: t.group_id } : {},
    };
  });

  return { data: enriched };
}

function generateMockCampaigns(sector) {
  const data = getSectorData(sector);
  const isBarSector = sector === "bares_deportes" || sector === "bares_tematicos";
  const isComidaParaLlevarSector = ["pizzeria", "comida_para_llevar", "restaurantes", "distribucion"].includes(
    (sector || "").toLowerCase().trim()
  );
  return [
    {
      campaign_id: { S: isBarSector ? "bar-clasico-2026" : "mock-campaign-001" },
      company_id: { S: SIM_COMPANY_ID },
      name: { S: isBarSector
        ? "El Clásico Barça-Madrid ⚽"
        : isComidaParaLlevarSector
          ? "Menú especial de Navidad 🎄"
          : `Campaña ${data.label} 🎯` },
      status: { S: "FINISHED" },
      created_at: { S: dayjs().subtract(5, "day").toISOString() },
      finished_at: { S: dayjs().subtract(4, "day").toISOString() },
      total: { N: "120" },
      template: { S: JSON.stringify({ name: isBarSector ? "bar_clasico" : "oferta_especial" }) },
      phases: { S: JSON.stringify(["Pendiente", "Contactado", "Interesado"]) },
      start_date: { S: dayjs().subtract(5, "day").format("YYYY-MM-DD") },
      per_day: { N: "0" },
      created_by: { S: "vendedor" },
      campaign_hmac: { S: "abc123" },
    },
    {
      campaign_id: { S: isBarSector ? "bar-halloween-2026" : "mock-campaign-002" },
      company_id: { S: SIM_COMPANY_ID },
      name: { S: isBarSector ? "Noche de Halloween 🎃" : "Campaña Fidelización Clientes 💎" },
      status: { S: "ACTIVE" },
      created_at: { S: dayjs().subtract(2, "day").toISOString() },
      total: { N: "85" },
      template: { S: JSON.stringify({ name: isBarSector ? "bar_halloween" : "descuento_fidelidad" }) },
      phases: { S: JSON.stringify(["Pendiente", "Contactado"]) },
      start_date: { S: dayjs().subtract(2, "day").format("YYYY-MM-DD") },
      per_day: { N: "30" },
      created_by: { S: "vendedor" },
      campaign_hmac: { S: "def456" },
    },
    (() => {
      const isLavadero = (sector || "").toLowerCase().trim() === "lavadero";
      const isBarSector = sector === "bares_deportes" || sector === "bares_tematicos";
      if (isLavadero) {
        return {
          campaign_id: { S: "pascuas-demo-2026" },
          company_id: { S: SIM_COMPANY_ID },
          name: { S: "Promoción lavado y detallado 🚗" },
          status: { S: "Pendiente" },
          created_at: { S: dayjs().toISOString() },
          finished_at: { S: null },
          total: { N: "1" },
          template: { S: JSON.stringify({ name: "promocion_lavado" }) },
          phases: { S: JSON.stringify(["Pendiente"]) },
          start_date: { S: "2026-04-01" },
          end_date: { S: "2026-04-30" },
          per_day: { N: "0" },
          created_by: { S: "vendedor" },
          campaign_hmac: { S: "" },
          description: {
            S: "15% en horario valle (mar–jue 10:00–12:00) y en detallado y limpieza",
          },
        };
      }
      if (isBarSector) {
        return {
          campaign_id: { S: "pascuas-demo-2026" },
          company_id: { S: SIM_COMPANY_ID },
          name: { S: "El Clásico – Barça vs Madrid ⚽" },
          status: { S: "Pendiente" },
          created_at: { S: dayjs().toISOString() },
          finished_at: { S: null },
          total: { N: "1" },
          template: { S: JSON.stringify({ name: "bar_clasico_live" }) },
          phases: { S: JSON.stringify(["Pendiente"]) },
          start_date: { S: "2026-04-03" },
          end_date: { S: "2026-04-03" },
          per_day: { N: "0" },
          created_by: { S: "vendedor" },
          campaign_hmac: { S: "" },
          description: { S: "Campaña para el partido Barça-Madrid en pantalla grande" },
        };
      }
      return {
        campaign_id: { S: "pascuas-demo-2026" },
        company_id: { S: SIM_COMPANY_ID },
        name: { S: "Felices Pascuas – Cierre 🐣" },
        status: { S: "Pendiente" },
        created_at: { S: dayjs().toISOString() },
        finished_at: { S: null },
        total: { N: "1" },
        template: { S: JSON.stringify({ name: "pascuas_cierre" }) },
        phases: { S: JSON.stringify(["Pendiente"]) },
        start_date: { S: "2026-04-03" },
        end_date: { S: "2026-04-07" },
        per_day: { N: "0" },
        created_by: { S: "vendedor" },
        campaign_hmac: { S: "" },
        description: { S: "Campaña de Pascuas para informar del cierre" },
      };
    })(),
  ];
}

function generateMockCampaignTracking(sector) {
  const data = getSectorData(sector);
  const phases = ["Pendiente", "Contactado", "Cerrado", "No interesado"];
  const contacts = data.clientNames.map((name, i) => {
    const phone = generateMockPhone(i, name);
    return {
      contact_id: `${SIM_COMPANY_ID}#${phone}`,
      nombre: name,
      phone: phone,
      phase: phases[i % phases.length],
      updated_at: dayjs().subtract(Math.floor(i / 4), "day").toISOString(),
    };
  });

  return {
    campaign_id: "mock-campaign-001",
    name: `Campaña ${getSectorData(sector).label}`,
    total_contacts: contacts.length,
    phases,
    status: "FINISHED",
    created_at: dayjs().subtract(5, "day").toISOString(),
    contacts,
  };
}

// Flat list of all sports list names for bares_deportes (derived at module load)
const BAR_DEPORTES_GROUP_NAMES = [
  // La Liga
  "Real Madrid", "FC Barcelona", "Atlético de Madrid", "Athletic Club",
  "Real Sociedad", "Real Betis", "Villarreal CF", "Valencia CF", "Sevilla FC", "RC Celta",
  // Premier League
  "Manchester City", "Arsenal", "Chelsea", "Liverpool", "Manchester United",
  "Tottenham Hotspur", "Newcastle United", "Aston Villa",
  // ACB
  "Real Madrid Basket", "FC Barcelona Basket", "Unicaja Málaga", "Joventut Badalona",
  "Baskonia Vitoria", "Gran Canaria Claret", "Valencia Basket", "Baxi Manresa",
  // NBA
  "Los Angeles Lakers", "Golden State Warriors", "Boston Celtics", "Miami Heat", "Chicago Bulls",
  // F1
  "Max Verstappen (Red Bull)", "Lewis Hamilton (Ferrari)", "Lando Norris (McLaren)", "Charles Leclerc (Ferrari)",
  "Carlos Sainz (Williams)", "Fernando Alonso (Aston Martin)", "Red Bull Racing", "McLaren F1 Team",
  // Tenis
  "Carlos Alcaraz", "Rafa Nadal (fans)", "Novak Djokovic", "Jannik Sinner",
  "Daniil Medvedev", "Roland Garros", "Wimbledon", "US Open",
  // Pádel
  "Juan Lebrón", "Alejandro Galán", "Arturo Coello / Tapia", "Premier Padel — General",
  // Ciclismo
  "Vuelta a España", "Tadej Pogačar (fans)", "Primož Roglič (fans)",
];

export function generateMockGroups(sector) {
  if (sector === "bares_deportes") {
    const flat = [];
    for (const sport of BAR_DEPORTES_LISTS) {
      flat.push({ nombre: sport.nombre, icon: "tag", is_category: true });
      for (const child of sport.children) {
        if (child.is_category) {
          flat.push({ nombre: child.nombre, icon: "tag", is_category: true, parent: sport.nombre });
          for (const leaf of child.children) {
            flat.push({ nombre: leaf.nombre, icon: "users", parent: child.nombre });
          }
        } else {
          flat.push({ nombre: child.nombre, icon: "users", parent: sport.nombre });
        }
      }
    }
    return flat;
  }
  return [
    { nombre: "general", icon: "list" },
    { nombre: "VIP", icon: "star" },
    { nombre: "Clientes", icon: "users" },
    { nombre: "Frecuente", icon: "repeat" },
  ];
}

function generateMockMessages(sector) {
  const conversations = isFoodOrdersSector(sector)
    ? [
        { out: "¡Hola! ¿Qué te pongo hoy?", inp: "Hola, quería hacer un pedido para recoger" },
        { out: "¡Claro! Dime qué quieres pedir.", inp: "Dos pizzas margarita y una de barbacoa" },
        { out: "Perfecto. Son 32€. ¿Lo recoges o lo llevamos?", inp: "Paso a recogerlo, gracias" },
        { out: "Estará listo en 25 minutos. Te avisamos cuando esté.", inp: "Genial, muchas gracias!" },
        { out: "¡Tu pedido ya está listo para recoger! 🍕", inp: "Voy para allá 😊" },
      ]
    : [
        { out: "¡Hola! ¿En qué puedo ayudarte?", inp: "Me gustaría pedir cita para esta semana" },
        { out: "Perfecto, ¿qué servicio te interesa?", inp: "Para el jueves si es posible" },
        { out: "El jueves a las 15:00 hay disponibilidad. ¿Te va bien?", inp: "Perfecto, gracias!" },
        { out: "Reservado. Te enviamos confirmación.", inp: "Nos vemos el jueves!" },
        { out: "¡Hasta el jueves!", inp: "Muchas gracias 😊" },
      ];

  const messages = [];
  const now = dayjs();
  conversations.forEach((conv, i) => {
    const daysAgo = Math.floor(i / 2) + 1;
    messages.push({
      mensaje_id: `mock-out-${i}`,
      timestamp: now.subtract(daysAgo, "day").subtract(i * 10, "minute").toISOString(),
      messageBody: conv.out,
      messageType: "text",
      fromMe: true,
      direction: "outbound",
      status: "delivered",
    });
    messages.push({
      mensaje_id: `mock-in-${i}`,
      timestamp: now.subtract(daysAgo, "day").subtract(i * 10 - 2, "minute").toISOString(),
      messageBody: conv.inp,
      messageType: "text",
      fromMe: false,
      direction: "inbound",
      status: "read",
    });
  });

  messages.sort((a, b) => a.timestamp.localeCompare(b.timestamp));
  return { items: messages, lastKey: null };
}

function generateMockCompanySettings(sector) {
  return {
    settings: {
      template_groups: {
        es: [
          { id: "comunicaciones", nombre: "Comunicaciones", descripcion: "Recordatorios y bienvenidas", icon: "MessageSquare" },
          { id: "promociones", nombre: "Ofertas y Descuentos", descripcion: "Promociones y fidelización", icon: "Tag" },
          { id: "festividades", nombre: "Festividades", descripcion: "Cumpleaños y fechas especiales", icon: "Calendar" },
          { id: "avisos", nombre: "Avisos", descripcion: "Horarios y cierres", icon: "Clock" },
        ],
      },
      timezone: "Europe/Madrid",
      language: "es",
    },
  };
}

function generateMockDashboard(sector) {
  return {
    appointments_today: 12,
    appointments_week: 48,
    new_clients_month: 18,
    revenue_month: 2340,
    occupation_rate: 78,
    messages_sent: 89,
  };
}

function generateMockEstadisticas(from, to, granularity = "daily", sector) {
  const start = dayjs(from || dayjs().subtract(29, "day"));
  const end = dayjs(to || dayjs());
  const rows = [];
  let current = start.startOf(granularity === "monthly" ? "month" : "day");

  const marketingTemplates = [
    "feliz_pascua",
    "oferta_especial",
    "bienvenida_cliente",
    "descuento_fidelidad",
    "feliz_cumpleanos",
    "encuesta_satisfaccion",
    "promocion_verano",
    "black_friday",
  ];

  const utilityTemplates = [
    "recordatorio_cita",
    "confirmacion_reserva",
    "horario_festivo",
    "cierre_vacaciones",
    "cancelacion_cita",
    "cambio_cita",
    "invoice",
    "receipt",
  ];

  while (current.isBefore(end) || current.isSame(end, granularity === "monthly" ? "month" : "day")) {
    const row = {
      stat_date: granularity === "monthly" ? current.format("YYYY-MM") : current.format("YYYY-MM-DD"),
      // Pedidos creados: serie temporal coherente. Solo se muestra en el dashboard
      // de food-orders (getAvailableMetrics la añade únicamente para ese sector),
      // así que incluirla siempre aquí es inocuo para el resto de verticales.
      orders_created: granularity === "monthly"
        ? Math.floor(Math.random() * 300 + 120)
        : Math.floor(Math.random() * 25 + 8),
      messages_sent_manual: Math.floor(Math.random() * 30 + 10),
      messages_sent_system: Math.floor(Math.random() * 20 + 5),
      messages_received: Math.floor(Math.random() * 40 + 20),
      appointments_created_crm: Math.floor(Math.random() * 10 + 2),
      appointments_created_whatsapp: Math.floor(Math.random() * 5),
      appointments_created_google: Math.floor(Math.random() * 4),
      appointments_confirmed: Math.floor(Math.random() * 10 + 1),
      appointments_cancelled: Math.floor(Math.random() * 3),
      texts_sent: Math.floor(Math.random() * 50),
      contacts_created: Math.floor(Math.random() * 5),
    };

    // Generar datos de plantillas MARKETING
    let marketingTotal = 0;
    marketingTemplates.slice(0, 4).forEach(tpl => {
      const count = Math.floor(Math.random() * 2 + 1); // 1-2 por plantilla
      row[`tpl#MARKETING#${tpl}`] = count;
      marketingTotal += count;
    });

    // Generar datos de plantillas UTILITY
    let utilityTotal = 0;
    utilityTemplates.slice(0, 2).forEach(tpl => {
      const count = Math.floor(Math.random() * 2); // 0-1 por plantilla
      row[`tpl#UTILITY#${tpl}`] = count;
      utilityTotal += count;
    });

    // Total de plantillas
    row.templates_sent = marketingTotal + utilityTotal;

    // Convertir a formato DynamoDB (S para strings, N para números)
    rows.push(
      Object.fromEntries(
        Object.entries(row).map(([k, v]) => [
          k,
          typeof v === "number" ? { N: v.toString() } : { S: v }
        ])
      )
    );

    current = current.add(1, granularity === "monthly" ? "month" : "day");
  }

  return rows;
}

function generateMockWhatsappStatus(sector) {
  // Sectores con número de Conext dedicado a citas (barbería, peluquerías,
  // estética, lavadero): el simulador muestra el número del SECTOR y su enlace
  // de reserva, no el número demo central. El cliente ya no conecta su número.
  const info = getSectorBookingInfo(sector);
  if (info) {
    return {
      phoneConnected: true,
      records: [
        {
          company_id: "conext-citas",
          phone_number_id: info.phone_number_id,
          number: info.e164,
          whatsapp_business_id: "2906560722875557",
          meta_app_id: "459702239951648",
          createdAt: "2026-01-16T14:37:18.864Z",
          updatedAt: dayjs().toISOString(),
          qr: {
            code: MOCK_BOOKING_CODE,
            prefilled_message: info.prefilled_message,
            // El cartel genera el QR desde deep_link_url (qrcode.react); image_url
            // queda vacío (no usamos QR pre-renderizado de un tercero).
            deep_link_url: info.deep_link_url,
            image_url: "",
            language: "es",
            updated_at: dayjs().toISOString(),
          },
        },
      ],
    };
  }

  // Resto de sectores: número demo central de Conext (fallback). En food-orders
  // el QR es para PEDIDOS (no citas), coherente con que es una app de pedidos.
  const food = isFoodOrdersSector(sector);
  const qrPrefill = food ? "Hola, quiero hacer un pedido." : "Hola, me gustaría agendar una cita de prueba.";
  const qrLink = food
    ? `https://wa.me/34960600024?text=${encodeURIComponent(qrPrefill)}`
    : "https://wa.me/message/55KFSLPM2XZ6D1";
  return {
    phoneConnected: true,
    records: [
      {
        company_id: "conextcrm-e2f0b3",
        phone_number_id: "911093782093395",
        number: "+34960600024",
        whatsapp_business_id: "2906560722875557",
        meta_app_id: "459702239951648",
        createdAt: "2026-01-16T14:37:18.864Z",
        updatedAt: dayjs().toISOString(),
        qr: {
          code: "55KFSLPM2XZ6D1",
          prefilled_message: qrPrefill,
          deep_link_url: qrLink,
          image_url: "https://api.qrserver.com/v1/create-qr-code/?size=320x320&data=https%3A%2F%2Fwa.me%2Fmessage%2F55KFSLPM2XZ6D1",
          language: "es",
          updated_at: dayjs().toISOString(),
        },
      },
    ],
  };
}

function generateMockEventHistory(contactId, sector) {
  const data = getSectorData(sector);
  const events = [];
  const now = dayjs();

  // Generamos algunas citas pasadas y una futura
  for (let i = 0; i < 5; i++) {
    const isFuture = i === 0;
    const date = isFuture ? now.add(2, "day") : now.subtract((i + 1) * 7, "day");
    const service = data.services[i % data.services.length];
    const worker = data.workers[i % data.workers.length];

    events.push({
      event_id: `mock-hist-${contactId}-${i}`,
      start: date.hour(10 + i).minute(0).toISOString(),
      end: date.hour(10 + i).minute(30).toISOString(),
      service_name: service.name,
      worker_name: worker.name,
      status: isFuture ? "confirmed" : "attended",
      price: service.price,
      invoiced: !isFuture && i % 2 === 0,
    });
  }

  return events;
}

function generateMockRecurringSeries(contactId, sector) {
  const data = getSectorData(sector);
  if (contactId === "mock-no-recurring") return [];

  const service1 = data.services[0];
  const service2 = data.services[Math.min(1, data.services.length - 1)];
  const names = data.clientNames || [];
  const name1 = names[2] || "Laura Martínez";
  const name2 = names[5] || "Elena González";

  const startDate1 = dayjs().subtract(10, "week").format("YYYY-MM-DD");
  const startDate2 = dayjs().subtract(3, "month").format("YYYY-MM-DD");

  return [
    {
      recurrence_id: `rec-${contactId}-1`,
      contact_id: contactId,
      customer_name: name1,
      service_id: service1.id,
      service_name: service1.name,
      status: "ACTIVE",
      frequency: "WEEKLY",
      interval: 1,
      interval_unit: "week",
      start_time: "10:30",
      start_date: startDate1,
      created_count: 10,
      upcoming_occurrences: [
        { date: dayjs().add(1, "week").format("YYYY-MM-DD"), time: "10:30" },
        { date: dayjs().add(2, "week").format("YYYY-MM-DD"), time: "10:30" },
        { date: dayjs().add(3, "week").format("YYYY-MM-DD"), time: "10:30" },
      ],
    },
    {
      recurrence_id: `rec-${contactId}-2`,
      contact_id: contactId,
      customer_name: name2,
      service_id: service2.id,
      service_name: service2.name,
      status: "PAUSED",
      frequency: "BIWEEKLY",
      interval: 2,
      interval_unit: "week",
      start_time: "17:00",
      start_date: startDate2,
      end_date: dayjs().add(3, "month").format("YYYY-MM-DD"),
      created_count: 5,
      upcoming_occurrences: [
        { date: dayjs().add(2, "week").format("YYYY-MM-DD"), time: "17:00" },
        { date: dayjs().add(4, "week").format("YYYY-MM-DD"), time: "17:00" },
      ],
    },
  ];
}

/* ─── Función principal ─────────────────────────────────────── */

export const getMockData = (url, params = {}, sector = "barberia") => {
  // Historial de citas por participante (nuevo)
  if (url.includes("/calendar/events-by-participant")) {
    const contactId = params.participant_id || "mock-contact";
    return { items: generateMockEventHistory(contactId, sector) };
  }
  // Mensajes de chat individuales
  if (url.match(/\/chat\/[^/?]+\/messages/) || url.match(/\/intranet-chat\/[^/?]+\/messages/)) {
    // Pedidos: cada chat es el de un pedido real del tablero (Formulario, ticket, avisos).
    if (isFoodOrdersSector(sector)) {
      const m = url.match(/\/chat\/([^/?]+)\/messages/);
      const food = m ? buildFoodChatMessages(SIM_COMPANY_ID, m[1]) : null;
      if (food) return food;
    }
    return generateMockMessages(sector);
  }
  // Lista de chats unificada
  if (url.includes("/chat-list/unified")) {
    if (isFoodOrdersSector(sector)) return { items: buildFoodChatList(SIM_COMPANY_ID), lastKey: null };
    return { items: generateMockChats(sector), lastKey: null };
  }
  // Plantillas de WhatsApp
  if (url.includes("/plantillas")) {
    return generateMockTemplates(sector);
  }
  // Campañas (pero NO campaign-tracking, que tiene su propio check abajo)
  if (url.includes("/campaigns") && !url.includes("/campaign-tracking")) {
    return generateMockCampaigns(sector);
  }
  // Grupos de contactos
  if (url.includes("/grupos")) {
    return { grupos: generateMockGroups(sector) };
  }
  // Tracking de campañas
  if (url.includes("/campaign-tracking")) {
    return generateMockCampaignTracking(sector);
  }
  // WhatsApp connection status (Meta)
  if (url.includes("/meta/whatsapp/status")) {
    return generateMockWhatsappStatus(sector);
  }
  // Citas recurrentes
  if (url.includes("/peluqueria/recurring-series") || url.includes("/calendar/recurring-series")) {
    const contactId = params.contact_id || "mock-contact";
    return { series: generateMockRecurringSeries(contactId, sector) };
  }
  // Recordatorios (Reminders)
  if (url.includes("/reminders")) {
    const contactId = params.contact_id || params.participant_id || "mock-contact";
    const data = getSectorData(sector);
    const service1 = data.services[0] || { name: "Corte" };
    const service2 = data.services[1] || { name: "Barba" };
    
    return {
      reminders: [
        {
          schedule_name: "REMINDER#123",
          contact_id: contactId,
          contact_name: "María García",
          service_name: service1.name,
          fire_at: dayjs().add(2, "day").toISOString(),
          template_name: "recordatorio_reenganche",
        },
        {
          schedule_name: "REMINDER#456",
          contact_id: contactId,
          contact_name: "Juan López",
          service_name: service2.name,
          fire_at: dayjs().add(5, "day").toISOString(),
          template_name: "recordatorio_reenganche",
        },
      ],
    };
  }
  // Configuración de empresa (panel de ajustes)
  if (url.includes("/company/settings")) {
    return generateMockCompanySettings(sector);
  }
  // Dashboard
  if (url.includes("/dashboard")) {
    return generateMockDashboard(sector);
  }
  // Dashboard stats (el panel de métricas general)
  if (url.includes("/estadisticas")) {
    const { from, to, granularity } = params;
    return generateMockEstadisticas(from, to, granularity, sector);
  }
  // TODO: Availability endpoints (/peluqueria/available-days, /peluqueria/available-slots)
  // y contact endpoints (/contactos/search) se dejan pasar al backend real (devuelven null aquí).
  return null;
};

/* ─── Helpers exportados para SimulatorContext ──────────────── */

export const getWorkersForSector = (sector) => getSectorData(sector).workers;

export const getServicesForSector = (sector) => getSectorData(sector).services;

export const getContactsForSector = (sector) => {
  const data = getSectorData(sector);
  return data.clientNames.map((name, i) => {
    const service = data.services.length > 0 ? data.services[i % data.services.length] : null;
    const daysAgo = 3 + (i % 14);

    const phone = generateMockPhone(i, name);

    // Generar customizations para algunos contactos (ej: indices 0, 2, 4)
    let serviceCustomizations = undefined;
    if (i % 2 === 0 && data.services.length > 0) {
      // Ej: para el primer y tercer cliente, personalizar el primer servicio
      const customService = data.services[0];
      const customizationExample = {
        serviceId: customService.id,
        serviceName: customService.name,
        customDuration: customService.duration + 15,
        standardDuration: customService.duration,
        isInterleaved: i === 0, // Solo el primer contacto tendrá intercalado
        interleavedBlocks: i === 0 ? [
          { id: 1, type: "work", duration: 20, label: "Trabajo" },
          { id: 2, type: "processing", duration: 10, label: "Espera" },
          { id: 3, type: "work", duration: customService.duration + 15 - 30, label: "Trabajo" },
        ] : [],
      };

      serviceCustomizations = {
        M: {
          [customService.id]: {
            M: {
              serviceId: { S: customizationExample.serviceId },
              serviceName: { S: customizationExample.serviceName },
              customDuration: { N: String(customizationExample.customDuration) },
              standardDuration: { N: String(customizationExample.standardDuration) },
              isInterleaved: { BOOL: customizationExample.isInterleaved },
              interleavedBlocks: {
                L: customizationExample.interleavedBlocks.map((block) => ({
                  M: {
                    id: { N: String(block.id) },
                    type: { S: block.type },
                    duration: { N: String(block.duration) },
                    label: { S: block.label },
                  },
                })),
              },
              // Add some demo reminder/recurrence data for simulator visualization
              reminder_days: { N: String(i % 3 === 0 ? 14 : 7) },
              // For a subset, add a simple recurrence object to show 'Recurrente' chips
              ...(i % 4 === 0
                ? {
                    recurrence: {
                      M: {
                        frequency: { S: "WEEKLY" },
                        interval: { N: "1" },
                        start_date: { S: dayjs().add(1, "day").format("YYYY-MM-DD") },
                      },
                    },
                  }
                : {}),
            },
          },
        },
      };
    }

    const contact = {
      // DynamoDB-style format that Contactos.jsx expects (c.nombre?.S etc.)
      contact_id: { S: `${SIM_COMPANY_ID}#${phone}` },
      full_name: { S: name },
      nombre: { S: name },
      phone_number: { S: phone },
      email: { S: `${name.toLowerCase().replace(/\s+/g, ".")}@ejemplo.com` },
      grupo: {
        SS: sector === "bares_deportes"
          // Each contact belongs to 1-2 sports lists based on index
          ? (() => {
              const primary = BAR_DEPORTES_GROUP_NAMES[i % BAR_DEPORTES_GROUP_NAMES.length];
              const secondary = BAR_DEPORTES_GROUP_NAMES[(i + 7) % BAR_DEPORTES_GROUP_NAMES.length];
              return primary !== secondary ? [primary, secondary] : [primary];
            })()
          : [["general", "VIP", "Clientes", "Frecuente"][i % 4]],
      },
      created_at: { S: dayjs().subtract(30 + i, "day").toISOString() },
      updated_at: { S: dayjs().toISOString() },
      // Cita previa simulada
      last_appointment: {
        is_future: false,
        date_str: dayjs().subtract(daysAgo, "day").format("DD/MM/YYYY"),
        service: service?.name ?? null,
      },
    };

    // Añadir género explícito para contactos conocidos en el simulador
    if (name === "Carmen García") {
      contact.genero = { S: "mujer" };
      contact.email = { S: "carmen.garcía@ejemplo.com" };
    }
    if (name === "Javier Jiménez") {
      contact.genero = { S: "hombre" };
    }

    // Notas adicionales demo: algunos contactos tienen nota para mostrar el
    // chip "Ver nota adicional" en la tarjeta (abre el modal enfocando Notas).
    if (i === 0) {
      contact.notas = {
        S: "Cliente habitual. Prefiere cita por la mañana y siempre con el mismo profesional.",
      };
    } else if (i === 2) {
      contact.notas = {
        S: "Sensible a algunos productos. Confirmar antes de aplicar.",
      };
    }

    // Saldo pendiente demo: algunos contactos deben dinero (negativo = deuda)
    if (name === "Carmen García") {
      contact.saldo_pendiente = { N: "-45.5" };
    } else if (name === "Javier Jiménez") {
      contact.saldo_pendiente = { N: "-120" };
    } else if (i === 2) {
      contact.saldo_pendiente = { N: "-18.75" };
    }
    // Vehículos mock para varios contactos en sector lavadero (demo búsqueda por matrícula)
    if (sector === "lavadero") {
      const LAV_VEHICLES = {
        "Javier Jiménez": [
          { id: "v-jav-1", brand: "Seat", model: "León", plate: "4823 KJT", size: "mediano" },
          { id: "v-jav-2", brand: "Volkswagen", model: "Touareg", plate: "9901 MNA", size: "grande" },
        ],
      };
      // Asignar un vehículo determinista a los primeros contactos sin vehículo explícito.
      const FALLBACK_PLATES = [
        { brand: "Renault", model: "Clio", plate: "1234 BCD", size: "pequeno" },
        { brand: "Toyota", model: "Corolla", plate: "5678 FGH", size: "mediano" },
        { brand: "Ford", model: "Focus", plate: "2468 JKL", size: "mediano" },
        { brand: "BMW", model: "X5", plate: "1357 MNP", size: "grande" },
      ];
      const explicit = LAV_VEHICLES[name];
      if (explicit) {
        contact.vehicles = { S: JSON.stringify(explicit) };
      } else if (i < FALLBACK_PLATES.length) {
        const fv = FALLBACK_PLATES[i];
        contact.vehicles = { S: JSON.stringify([{ id: `v-${i}`, ...fv }]) };
      }
    }

    // Añadir customizations si las hay
    if (serviceCustomizations) {
      contact.serviceCustomizations = serviceCustomizations;
    }

    return contact;
  });
};

export const SECTOR_DATA_MAP = SECTOR_DATA;

export const BAR_DEPORTES_LISTS = [
  {
    grupo_id: "cat-futbol",
    nombre: "Fútbol",
    is_category: true,
    children: [
      {
        grupo_id: "cat-la-liga",
        nombre: "La Liga",
        is_category: true,
        parent_id: "cat-futbol",
        children: [
          { grupo_id: "g-rmadrid",    nombre: "Real Madrid",        count: 42 },
          { grupo_id: "g-barca",      nombre: "FC Barcelona",       count: 38 },
          { grupo_id: "g-atletico",   nombre: "Atlético de Madrid", count: 31 },
          { grupo_id: "g-athletic",   nombre: "Athletic Club",      count: 18 },
          { grupo_id: "g-rsociedad",  nombre: "Real Sociedad",      count: 12 },
          { grupo_id: "g-betis",      nombre: "Real Betis",         count: 22 },
          { grupo_id: "g-villarreal", nombre: "Villarreal CF",      count: 9  },
          { grupo_id: "g-valencia",   nombre: "Valencia CF",        count: 27 },
          { grupo_id: "g-sevilla",    nombre: "Sevilla FC",         count: 15 },
          { grupo_id: "g-celta",      nombre: "RC Celta",           count: 8  },
        ],
      },
      {
        grupo_id: "cat-premier",
        nombre: "Premier League",
        is_category: true,
        parent_id: "cat-futbol",
        children: [
          { grupo_id: "g-mancity",    nombre: "Manchester City",    count: 35 },
          { grupo_id: "g-arsenal",    nombre: "Arsenal",            count: 29 },
          { grupo_id: "g-chelsea",    nombre: "Chelsea",            count: 24 },
          { grupo_id: "g-liverpool",  nombre: "Liverpool",          count: 41 },
          { grupo_id: "g-manutd",     nombre: "Manchester United",  count: 33 },
          { grupo_id: "g-spurs",      nombre: "Tottenham Hotspur",  count: 16 },
          { grupo_id: "g-newcastle",  nombre: "Newcastle United",   count: 14 },
          { grupo_id: "g-astonvilla", nombre: "Aston Villa",        count: 11 },
        ],
      },
    ],
  },
  {
    grupo_id: "cat-basket",
    nombre: "Baloncesto",
    is_category: true,
    children: [
      {
        grupo_id: "cat-acb",
        nombre: "ACB — Liga Endesa",
        is_category: true,
        parent_id: "cat-basket",
        children: [
          { grupo_id: "g-rmbasket",  nombre: "Real Madrid Basket",    count: 19 },
          { grupo_id: "g-fcbbasket", nombre: "FC Barcelona Basket",   count: 16 },
          { grupo_id: "g-unicaja",   nombre: "Unicaja Málaga",        count: 7  },
          { grupo_id: "g-joventut",  nombre: "Joventut Badalona",     count: 5  },
          { grupo_id: "g-baskonia",  nombre: "Baskonia Vitoria",      count: 6  },
          { grupo_id: "g-grancan",   nombre: "Gran Canaria Claret",   count: 4  },
          { grupo_id: "g-valbasket", nombre: "Valencia Basket",       count: 11 },
          { grupo_id: "g-manresa",   nombre: "Baxi Manresa",          count: 3  },
        ],
      },
      {
        grupo_id: "cat-nba",
        nombre: "NBA",
        is_category: true,
        parent_id: "cat-basket",
        children: [
          { grupo_id: "g-lakers",    nombre: "Los Angeles Lakers",    count: 28 },
          { grupo_id: "g-warriors",  nombre: "Golden State Warriors", count: 22 },
          { grupo_id: "g-celtics",   nombre: "Boston Celtics",        count: 19 },
          { grupo_id: "g-heat",      nombre: "Miami Heat",            count: 15 },
          { grupo_id: "g-bulls",     nombre: "Chicago Bulls",         count: 12 },
        ],
      },
    ],
  },
  {
    grupo_id: "cat-f1",
    nombre: "Fórmula 1",
    is_category: true,
    children: [
      { grupo_id: "g-verstappen",  nombre: "Max Verstappen (Red Bull)",      count: 24 },
      { grupo_id: "g-hamilton",    nombre: "Lewis Hamilton (Ferrari)",       count: 31 },
      { grupo_id: "g-norris",      nombre: "Lando Norris (McLaren)",         count: 18 },
      { grupo_id: "g-leclerc",     nombre: "Charles Leclerc (Ferrari)",      count: 22 },
      { grupo_id: "g-sainz",       nombre: "Carlos Sainz (Williams)",        count: 29 },
      { grupo_id: "g-alonso",      nombre: "Fernando Alonso (Aston Martin)", count: 37 },
      { grupo_id: "g-redbull",     nombre: "Red Bull Racing",                count: 19 },
      { grupo_id: "g-mclaren",     nombre: "McLaren F1 Team",                count: 14 },
    ],
  },
  {
    grupo_id: "cat-tenis",
    nombre: "Tenis",
    is_category: true,
    children: [
      { grupo_id: "g-alcaraz",      nombre: "Carlos Alcaraz",               count: 41 },
      { grupo_id: "g-nadal",        nombre: "Rafa Nadal (fans)",            count: 53 },
      { grupo_id: "g-djokovic",     nombre: "Novak Djokovic",               count: 28 },
      { grupo_id: "g-sinner",       nombre: "Jannik Sinner",                count: 16 },
      { grupo_id: "g-medvedev",     nombre: "Daniil Medvedev",              count: 11 },
      { grupo_id: "g-rolandgarros", nombre: "Roland Garros",                count: 34 },
      { grupo_id: "g-wimbledon",    nombre: "Wimbledon",                    count: 22 },
      { grupo_id: "g-usopen",       nombre: "US Open",                      count: 17 },
    ],
  },
  {
    grupo_id: "cat-padel",
    nombre: "Pádel",
    is_category: true,
    children: [
      { grupo_id: "g-lebron",       nombre: "Juan Lebrón",                  count: 19 },
      { grupo_id: "g-galan",        nombre: "Alejandro Galán",              count: 17 },
      { grupo_id: "g-tapia",        nombre: "Arturo Coello / Tapia",        count: 12 },
      { grupo_id: "g-premierpadel", nombre: "Premier Padel — General",      count: 26 },
    ],
  },
  {
    grupo_id: "cat-ciclismo",
    nombre: "Ciclismo",
    is_category: true,
    children: [
      { grupo_id: "g-vuelta",       nombre: "Vuelta a España",              count: 21 },
      { grupo_id: "g-pogacar",      nombre: "Tadej Pogačar (fans)",         count: 15 },
      { grupo_id: "g-roglic",       nombre: "Primož Roglič (fans)",         count: 9  },
    ],
  },
];
